import { lineGross, printedLineNote, resolveGstMode, resolvePlaceOfSupply } from '@jira-clone/shared-types';

export interface QuotationTokenData {
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  quotationNumber?: string;
  quotationDate?: string;
  salesPerson?: string;
  status?: string;
  subtotal?: number | string;
  taxTotal?: number | string;
  grandTotal?: number | string;
  paymentTerms?: string;
  notes?: string;
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyTagline?: string;
  companyTag?: string;
  preparedBy?: string;
  currency?: string;
  additionalCharges?: number;
  additionalChargesLabel?: string;
  contactDesignation?: string;
  footerLeft?: string;
  footerRight?: string;
  signatory?: string;
  signatoryRole?: string;
  items?: Array<{
    name?: string;
    description?: string;
    subDescription?: string;
    notes?: string;
    quantity?: number;
    unitPrice?: number;
    discountPercent?: number;
    rate?: number;
    lineTotal?: number;
    total?: number;
    cgstRate?: number;
    sgstRate?: number;
    igstRate?: number;
    catalogItemId?: string;
  }>;
}

export const SALES_DOCUMENT_TOKENS = [
  { token: '{{customerName}}', label: 'Customer Name', category: 'Document Data' },
  { token: '{{customerEmail}}', label: 'Customer Email', category: 'Document Data' },
  { token: '{{customerPhone}}', label: 'Customer Phone', category: 'Document Data' },
  { token: '{{customerAddress}}', label: 'Customer Address', category: 'Document Data' },
  { token: '{{quotationNumber}}', label: 'Quotation / Doc Number', category: 'Document Data' },
  { token: '{{quotationDate}}', label: 'Date', category: 'Document Data' },
  { token: '{{salesPerson}}', label: 'Sales Person', category: 'Document Data' },
  { token: '{{status}}', label: 'Status', category: 'Document Data' },
  { token: '{{subtotal}}', label: 'Subtotal Amount', category: 'Document Data' },
  { token: '{{taxTotal}}', label: 'Tax Amount', category: 'Document Data' },
  { token: '{{grandTotal}}', label: 'Grand Total Amount', category: 'Document Data' },
  { token: '{{paymentTerms}}', label: 'Payment Terms', category: 'Document Data' },
  { token: '{{notes}}', label: 'Notes', category: 'Document Data' },
  { token: '{{companyName}}', label: 'Company Name', category: 'Company Branding' },
  { token: '{{companyAddress}}', label: 'Company Address', category: 'Company Branding' },
  { token: '{{companyPhone}}', label: 'Company Phone', category: 'Company Branding' },
  { token: '{{companyEmail}}', label: 'Company Email', category: 'Company Branding' },
];

export function renderQuotationTemplate(html: string, data: QuotationTokenData): string {
  if (!html) return '';
  const formatMoney = (val: number | string | undefined) => {
    if (val === undefined || val === null) return '0.00';
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(num) ? '0.00' : num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  let rowsHtml = '';
  if (data.items && data.items.length > 0) {
    rowsHtml = data.items
      .map((item, idx) => {
        const name = item.name || item.description || 'Line Item';
        const qty = item.quantity || 1;
        const price = item.unitPrice || item.rate || 0;
        const disc = item.discountPercent || 0;
        const total = item.lineTotal || item.total || lineGross(qty, price, (item as any).unit) * (1 - disc / 100);
        return `
          <tr>
            <td class="c">${idx + 1}</td>
            <td>
              <div class="it-name">${name}</div>
              ${item.notes ? `<div class="it-desc">${item.notes}</div>` : ''}
            </td>
            <td class="n">${qty}</td>
            <td class="n">₹${formatMoney(price)}</td>
            <td class="n">${disc > 0 ? disc + '%' : '0%'}</td>
            <td class="n">₹${formatMoney(total)}</td>
          </tr>
        `;
      })
      .join('');
  } else {
    rowsHtml = `
      <tr>
        <td class="c">1</td>
        <td><div class="it-name">Services / Goods</div></td>
        <td class="n">1</td>
        <td class="n">₹${formatMoney(data.subtotal)}</td>
        <td class="n">0%</td>
        <td class="n">₹${formatMoney(data.subtotal)}</td>
      </tr>
    `;
  }
  const replacements: Record<string, string> = {
    '{{customerName}}': data.customerName || 'Client Name',
    '{{customerEmail}}': data.customerEmail || 'client@example.com',
    '{{customerPhone}}': data.customerPhone || 'N/A',
    '{{customerAddress}}': data.customerAddress || 'Client Address',
    '{{quotationNumber}}': data.quotationNumber || 'QTN-000',
    '{{quotationDate}}': data.quotationDate ? new Date(data.quotationDate).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN'),
    '{{salesPerson}}': data.salesPerson || 'Sales Team',
    '{{status}}': (data.status || 'DRAFT').toUpperCase(),
    '{{subtotal}}': formatMoney(data.subtotal),
    '{{taxTotal}}': formatMoney(data.taxTotal),
    '{{grandTotal}}': formatMoney(data.grandTotal),
    '{{paymentTerms}}': data.paymentTerms || 'Due on Receipt',
    '{{notes}}': data.notes || 'N/A',
    '{{companyName}}': data.companyName || 'DURRANI TECH PRIVATE LIMITED',
    '{{companyAddress}}': data.companyAddress || '23A, 2nd Floor, Yogesh House, Camp, Pune - 411001',
    '{{companyPhone}}': data.companyPhone || '+91 91309 77779',
    '{{companyEmail}}': data.companyEmail || 'info@durranitech.com',
    '{{itemsTableRows}}': rowsHtml,
  };
  let rendered = html;
  for (const [key, val] of Object.entries(replacements)) {
    rendered = rendered.split(key).join(val);
  }
  return rendered;
}

export function amountInWords(amount: number, currency: string = '₹'): string {
  const abs = Math.abs(Number(amount) || 0);
  const n = Math.floor(abs);
  if (n === 0 && Math.round((abs - n) * 100) === 0) {
    return currency === '₹' ? 'Zero Rupees Only' : 'Zero Only';
  }
  const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (x: number) => x < 20 ? ONES[x] : TENS[Math.floor(x / 10)] + (x % 10 ? ' ' + ONES[x % 10] : '');
  const three = (x: number) => x >= 100 ? ONES[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' + two(x % 100) : '') : two(x);

  const parts: string[] = [];
  const crore = Math.floor(n / 1e7);
  const lakh = Math.floor((n % 1e7) / 1e5);
  const thousand = Math.floor((n % 1e5) / 1e3);
  const rest = n % 1e3;
  if (crore) parts.push(three(crore) + ' Crore');
  if (lakh) parts.push(three(lakh) + ' Lakh');
  if (thousand) parts.push(three(thousand) + ' Thousand');
  if (rest) parts.push(three(rest));

  const paise = Math.round((abs - n) * 100);
  const unit = currency === '₹' ? ['Rupees', 'Paise'] : currency === '$' ? ['Dollars', 'Cents'] : ['Euro', 'Cents'];
  return (parts.join(' ') + ' ' + unit[0] + (paise ? ' and ' + two(paise) + ' ' + unit[1] : '') + ' Only').replace(/\s+/g, ' ').trim();
}

export function generateFullQuotationHtml(data: Parameters<typeof generateFullInvoiceHtml>[0]): string {
  return generateFullInvoiceHtml({ ...data, docType: 'quotation' });
}

export function generateFullProformaHtml(data: Parameters<typeof generateFullInvoiceHtml>[0]): string {
  return generateFullInvoiceHtml({ ...data, docType: 'proforma' });
}

export function generateFullInvoiceHtml(data: {
  docType?: 'tax' | 'proforma' | 'quotation';
  /** The copy heading on a tax invoice: the first issue is the original, later ones are copies. */
  copyLabel?: string;
  documentNumber?: string;
  number?: string;
  quotationNumber?: string;
  invoiceNumber?: string;
  proformaNumber?: string;
  poNumber?: string;
  quotationDate?: string;
  date?: string;
  dueDate?: string;
  validUntil?: string;
  paymentTerms?: string;
  /** The master-data description of the chosen payment term, printed after its name. */
  paymentTermsDescription?: string;
  reverseCharge?: string;
  referenceNo?: string;
  reference?: string;
  customerName?: string;
  clientName?: string;
  customerAddress?: string;
  clientAddress?: string;
  customerGstin?: string;
  clientGstin?: string;
  customerState?: string;
  clientState?: string;
  placeOfSupply?: string;
  customerContact?: string;
  customerEmail?: string;
  clientEmail?: string;
  customerPhone?: string;
  clientPhone?: string;
  subtotal?: number;
  taxableAmount?: number;
  taxTotal?: number;
  grandTotal?: number;
  total?: number;
  headerDiscount?: number;
  headerDiscountType?: 'percent' | 'fixed';
  additionalCharges?: number;
  additionalChargesLabel?: string;
  discount?: number;
  /** Full (unscaled) contract taxable amount — used as base for milestone table when stagePercent is set */
  contractTaxable?: number;
  /** Full (unscaled) contract grand total — used as base for milestone table when stagePercent is set */
  contractGrand?: number;
  /** Full (unscaled) contract tax total — used as base for milestone table when stagePercent is set */
  contractTax?: number;
  showHsn?: boolean;
  stagePercent?: number;
  milestoneIndex?: number;
  stageIndex?: number;
  milestoneName?: string;
  milestonePhaseLabel?: string;
  stageName?: string;
  stageAmountsAreFinal?: boolean;
  /**
   * Set when the document is a running bill — the bill for one stage of a milestone proforma
   * (numbered DT/RB/…). Its items and totals are already that stage's share, so nothing is scaled.
   */
  runningBill?: { stageNumber: number; stageCount: number; stageName?: string; percent?: number };
  /**
   * Running bill only: what is still unpaid on the other stages of the contract. It is added to
   * "Balance due", so the bill shows what the client still owes on the whole contract.
   */
  otherStagesDue?: number;
  /**
   * Set on the final tax invoice raised once every running bill is paid. It is the consolidated record of
   * the whole contract and lists the running bills it adjusts; GST was charged on those, not again here.
   */
  finalInvoice?: { bills: Array<{ number: string; stageNumber: number; stageName: string; percent?: number; taxable: number; tax: number; total: number; received?: number; paidAt?: string | null }> };
  gstEnabled?: boolean;
  gstMode?: 'intra' | 'inter' | 'none';
  /**
   * Names the document on its own heading. Used by estimate invoices, which are invoices raised
   * with no GST at all and must not read as a "Tax Invoice".
   */
  documentTitle?: string;
  currency?: string;
  items?: Array<{
    name?: string;
    description?: string;
    subDescription?: string;
    hsn?: string;
    hsnSac?: string;
    quantity?: number;
    qty?: number;
    unit?: string;
    unitPrice?: number;
    rate?: number;
    discountPercent?: number;
    discount?: number;
    disc?: number;
    gstRate?: number;
    gst?: number;
    catalogItemId?: string | null;
    image?: string | null;
    sku?: string;
  }>;
  notes?: string | string[];
  terms?: string | string[];
  termsList?: string[];
  assumptions?: string | string[];
  assumptionsList?: string[];
  /** Master-data switch: when false, the Assumptions & Exclusions block is omitted even for quotations. */
  assumptionsEnabled?: boolean;
  subject?: string;
  introText?: string;
  preparedBy?: string;
  contactPerson?: string;
  contactDesignation?: string;
  footerLeft?: string;
  footerRight?: string;
  paymentType?: 'full_payment' | 'milestone_based';
  milestones?: Array<{ name?: string; percent?: number; pct?: number; due?: string; exact?: boolean }>;
  amountReceived?: number;
  tdsRate?: number;
  bankAccName?: string;
  bankAccNo?: string;
  bankBranch?: string;
  bankName?: string;
  bankIfsc?: string;
  bankUpi?: string;
  declaration?: string;
  signatory?: string;
  signatoryRole?: string;
  signatureUrl?: string;
  companyName?: string;
  companyTagline?: string;
  companyTag?: string;
  companyAddress?: string;
  companyGstin?: string;
  companyPan?: string;
  companyState?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyWeb?: string;
  companyLogo?: string;
  logoUrl?: string;
  businessLogo?: string;
  shippingEnabled?: boolean;
  shippingName?: string;
  shippingAddress?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPincode?: string;
  shippingGstin?: string;
  displayOptions?: Record<string, any>;
  availableProducts?: Array<{
    _id?: string;
    id?: string;
    name?: string;
    description?: string;
    sellingPrice?: number;
    rate?: number;
    unitPrice?: number;
    hsnSac?: string;
    hsnCode?: string;
    hsn?: string;
    unit?: any;
    taxProfileId?: any;
    taxRate?: number;
    gst?: number;
    sku?: string;
  }>;
  isConfirmed?: boolean;
  isLocked?: boolean;
}): string {
  const isProforma   = data.docType === 'proforma';
  const isTax        = data.docType === 'tax';
  const isQuotation  = data.docType === 'quotation' || (!isProforma && !isTax);
  const docTypeVal   = isQuotation ? 'quotation' : isProforma ? 'proforma' : 'tax';

  // Quotations default to no GST unless explicitly enabled. Every other document
  // charges GST, by place of supply (CGST+SGST in the seller's state, IGST elsewhere).
  const gstByPlaceOfSupply = () =>
    resolveGstMode({
      gstEnabled: true,
      companyState: data.companyState,
      companyGstin: data.companyGstin,
      customerState: data.placeOfSupply || data.customerState || data.clientState,
      customerGstin: data.customerGstin || data.clientGstin,
      // Proforma invoices always display tax when gstEnabled is true — reverse charge is
      // informational only on a proforma and must not hide the tax breakdown.
      reverseCharge: isProforma ? false : data.reverseCharge,
    });
  if (data.gstEnabled !== undefined) {
    if (!data.gstEnabled) {
      data.gstMode = 'none';
    } else if (!data.gstMode || data.gstMode === 'none') {
      data.gstMode = gstByPlaceOfSupply();
    }
  } else if (isQuotation && !data.gstMode) {
    data.gstMode = 'none';
  } else if (!isQuotation && !data.gstMode) {
    data.gstMode = gstByPlaceOfSupply();
  }
  const isRunningBill = isTax && Boolean(data.runningBill);
  const isFinalInvoice = isTax && !isRunningBill && Boolean(data.finalInvoice);
  // An invoice raised with no tax charged (an estimate invoice) is not a GST invoice and must not
  // claim Section 31 of the CGST Act.
  const chargesNoTax = data.gstMode === 'none';
  const docTitle     = isQuotation ? 'Quotation' : isProforma ? 'Proforma Invoice' : isRunningBill ? 'Running Bill' : data.documentTitle || 'Tax Invoice';
  const docSub       = isQuotation
    ? 'Commercial Proposal · Quotation'
    : isProforma
    ? 'Estimate for advance payment · Not a demand for tax'
    : isFinalInvoice
    ? 'GST charged on the running bills below · Section 31, CGST Act'
    : isRunningBill
    ? `Stage ${data.runningBill!.stageNumber} of ${data.runningBill!.stageCount}${data.runningBill!.percent ? ` · ${data.runningBill!.percent}% of contract` : ''} · GST Invoice · Section 31, CGST Act`
    : chargesNoTax ? 'Invoice raised without GST' : 'GST Invoice · Section 31, CGST Act';
  // A re-download must not be able to pass itself off as the original, so the heading says which
  // copy this is. Callers pass it from the document's recorded download count.
  const copyLabel = data.copyLabel?.trim() || 'Original for Recipient';
  const noLabel      = isQuotation ? 'Quotation no.' : isProforma ? 'Proforma no.' : isRunningBill ? 'Running bill no.' : isFinalInvoice ? 'Invoice no.' : 'Invoice no.';
  const defaultNo    = isQuotation ? 'DT/QTN/2026-27/0001' : isProforma ? 'PI/2026-27/0001' : 'TI/2026-27/0001';

  // A proforma must never display a QT-prefixed number: the form derives its number from
  // the source quotation before the server applies the master-data proforma format, so
  // scrub the quotation prefix — `DT-QT-2026-27/0024` -> `DT-PI-2026-27/0024`.
  const scrubQuotationPrefix = (value?: string | null) =>
    value ? String(value).replace(/QTN?/gi, 'PI') : undefined;

  const dno = isProforma
    ? (
        scrubQuotationPrefix(data.proformaNumber) ||
        (data.documentNumber && !/^QT/i.test(data.documentNumber) ? scrubQuotationPrefix(data.documentNumber) : undefined) ||
        (data.number && !/^QT/i.test(data.number) ? scrubQuotationPrefix(data.number) : undefined) ||
        scrubQuotationPrefix(data.quotationNumber) ||
        scrubQuotationPrefix(data.documentNumber) ||
        'PI/2026-27/0001'
      )
    : isTax
    ? (
        data.invoiceNumber ||
        (data.documentNumber && !/^QT/i.test(data.documentNumber) && !/^PI/i.test(data.documentNumber) ? data.documentNumber : undefined) ||
        (data.number && !/^QT/i.test(data.number) && !/^PI/i.test(data.number) ? data.number : undefined) ||
        (data.quotationNumber ? data.quotationNumber.replace(/^QTN?/i, 'TI').replace(/QTN?/gi, 'TI') : undefined) ||
        (data.documentNumber ? data.documentNumber.replace(/^(?:QTN?|PI)/i, 'TI') : undefined) ||
        'TI/2026-27/0001'
      )
    : (
        data.quotationNumber ||
        (data.documentNumber && !/^(?:TI|PI|INV)/i.test(data.documentNumber) ? data.documentNumber : undefined) ||
        (data.number && !/^(?:TI|PI|INV)/i.test(data.number) ? data.number : undefined) ||
        (data.documentNumber && /^QT/i.test(data.documentNumber) ? data.documentNumber : undefined) ||
        defaultNo
      );

  const ddate      = data.date || data.quotationDate || new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const ddue       = data.dueDate || ddate;
  const dvalid     = data.validUntil || new Date(Date.now() + 15 * 864e5).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const revCharge  = data.reverseCharge || 'No';
  const refNo      = data.referenceNo || data.reference || data.poNumber || (isProforma && data.quotationNumber ? `Quotation #${data.quotationNumber}` : '—');

  const coName     = (data.companyName !== undefined && data.companyName !== '') ? data.companyName : '';
  const coTag      = data.companyTagline || data.companyTag || '';
  const coAddr     = (data.companyAddress !== undefined && data.companyAddress !== '') ? data.companyAddress : '';
  const coGstin    = (data.companyGstin !== undefined && data.companyGstin !== '') ? data.companyGstin.toUpperCase() : '';
  const coPan      = (data.companyPan !== undefined && data.companyPan !== '') ? data.companyPan.toUpperCase() : '';
  const coState    = (data.companyState !== undefined && data.companyState !== '') ? data.companyState : '';
  const coPhone    = (data.companyPhone !== undefined && data.companyPhone !== '') ? data.companyPhone : '';
  const coEmail    = (data.companyEmail !== undefined && data.companyEmail !== '') ? data.companyEmail : '';
  const coWeb      = (data.companyWeb !== undefined && data.companyWeb !== '') ? data.companyWeb : '';
  const coLogo     = data.companyLogo || data.logoUrl || data.businessLogo || '';

  const clientName  = (data.customerName !== undefined && data.customerName !== '') ? data.customerName : ((data.clientName !== undefined && data.clientName !== '') ? data.clientName : '');
  const clientAddr  = (data.customerAddress !== undefined) ? data.customerAddress : (data.clientAddress !== undefined ? data.clientAddress : '');
  const clientGstin = String((data.customerGstin !== undefined) ? data.customerGstin : (data.clientGstin !== undefined ? data.clientGstin : '') || '').toUpperCase();
  const clientState = data.customerState || data.clientState || '';
  // The place of supply is the state in the customer's GSTIN, else the State on the document.
  const placeResolved = resolvePlaceOfSupply({ customerGstin: clientGstin, customerState: data.placeOfSupply || clientState });
  const placeOfSupply = placeResolved.label || data.placeOfSupply || clientState || 'Not set';
  const placeFromGstin = placeResolved.source === 'gstin';
  const clientPhone = (data.customerContact !== undefined) ? data.customerContact : (data.clientPhone !== undefined ? data.clientPhone : (data.customerPhone !== undefined ? data.customerPhone : ''));
  const clientEmail = (data.clientEmail !== undefined) ? data.clientEmail : (data.customerEmail !== undefined ? data.customerEmail : '');
  const preparedBy = data.preparedBy || '';
  const footerLeft = data.footerLeft || '';
  const footerRight = data.footerRight || '';

  const rawItems = (Array.isArray(data.items) && data.items.length > 0)
    ? data.items
    : (Array.isArray((data as any).lines) && (data as any).lines.length > 0)
      ? (data as any).lines
      : (Array.isArray((data as any).template_data?.items) && (data as any).template_data.items.length > 0)
        ? (data as any).template_data.items
        : (Array.isArray((data as any).template_data?.lines) && (data as any).template_data.lines.length > 0)
          ? (data as any).template_data.lines
          : [];
  const defaultItems: Array<{ description: string; subDescription?: string; hsn: string; qty: number; unit: string; rate: number; disc: number; gst: number }> = [];

  const rawProducts = data.availableProducts || [];
  // Only the products passed in; no built-in sample catalogue.
  const productsList: any[] = Array.isArray(rawProducts) ? rawProducts : [];

  // Per-document display options (Advanced Options in the quotation form). Keys left
  // undefined fall back to the legacy rendering so older callers keep their layout.
  const dopts = (data.displayOptions || {}) as Record<string, any>;
  const optShowHsn = dopts.showHsnColumn !== undefined
    ? Boolean(dopts.showHsnColumn)
    : (data.showHsn !== undefined ? Boolean(data.showHsn) : true);
  const optShowTaxSummary = dopts.showTaxSummary !== undefined ? Boolean(dopts.showTaxSummary) : true;
  const optShowSku = Boolean(dopts.showSkuColumn);
  const optShowSerial = dopts.showSerialNumbers !== undefined ? Boolean(dopts.showSerialNumbers) : true;
  const optShowThumbs = Boolean(dopts.showThumbnails);
  const optDescFullWidth = Boolean(dopts.showDescFullWidth);
  const optHideSubtotal = Boolean(dopts.hideSubtotalForGroups);
  const optHidePlaceOfSupply = Boolean(dopts.hidePlaceOfSupply);
  const optFullUnits = dopts.displayUnit === 'full';

  // "Display unit as: Full (Numbers)" expands common short unit codes; full words pass through.
  const UNIT_FULL_FORM: Record<string, string> = {
    'no': 'Numbers', 'nos': 'Numbers', 'pcs': 'Pieces', 'pc': 'Pieces', 'pkg': 'Package',
    'hr': 'Hours', 'hrs': 'Hours', 'wk': 'Weeks', 'wks': 'Weeks', 'mo': 'Months', 'mos': 'Months',
    'yr': 'Years', 'yrs': 'Years', 'kg': 'Kilograms', 'km': 'Kilometres', 'm': 'Metres', 'cm': 'Centimetres',
    'ltr': 'Litres', 'set': 'Sets', 'box': 'Boxes', 'pkt': 'Packets',
  };
  const unitLabel = (u: any) => {
    const raw = String(u || 'Nos').trim();
    if (!optFullUnits) return raw;
    const key = raw.toLowerCase().replace(/\.$/, '');
    return UNIT_FULL_FORM[key] || raw;
  };

  const escapeAttr = (s: any) => String(s ?? '').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const productsOptionsHtml = productsList.map((p: any) => {
    const id = p._id || p.id || p.name;
    const name = p.name || p.description || 'Item';
    const desc = '';
    const hsn = p.hsnSac || p.hsnCode || p.hsn || '998314';
    const rate = p.sellingPrice ?? p.rate ?? p.unitPrice ?? 0;
    const unit = p.unit?.shortName || p.unit?.name || (typeof p.unit === 'string' ? p.unit : 'Nos');
    let gst = 18;
    if (p.taxProfileId && typeof p.taxProfileId === 'object') {
      gst = p.taxProfileId.igstRate || ((p.taxProfileId.cgstRate || 0) + (p.taxProfileId.sgstRate || 0)) || 18;
    } else if (p.taxRate !== undefined) {
      gst = p.taxRate;
    } else if (p.gst !== undefined) {
      gst = p.gst;
    }
    const label = name + (rate > 0 ? ` (₹${Number(rate).toLocaleString('en-IN')})` : '');
    return `<option value="${escapeAttr(id)}" data-name="${escapeAttr(name)}" data-desc="${escapeAttr(desc)}" data-hsn="${escapeAttr(hsn)}" data-rate="${rate}" data-unit="${escapeAttr(unit)}" data-gst="${gst}" data-disc="0">${escapeAttr(label)}</option>`;
  }).join('');

  const rawMilestonesForStage = Array.isArray(data.milestones) ? data.milestones : [];
  const activeMilestone = Number.isInteger(Number(data.milestoneIndex))
    ? rawMilestonesForStage[Number(data.milestoneIndex)]
    : undefined;
  const stageValue = (minorKey: string, valueKey: string): number | undefined => {
    const minor = Number((activeMilestone as any)?.[minorKey]);
    if (Number.isFinite(minor) && minor > 0) return minor / 100;
    const value = Number((activeMilestone as any)?.[valueKey]);
    return Number.isFinite(value) && value > 0 ? value : undefined;
  };
  const useStageOverrides = !data.stageAmountsAreFinal;
  const stageTaxableOverride = useStageOverrides ? (stageValue('milestoneTaxableAmountMinor', 'netAmount') ?? stageValue('milestoneTaxableAmountMinor', 'milestoneTaxableAmount')) : undefined;
  const stageTaxOverride = useStageOverrides ? (stageValue('milestoneGSTMinor', 'taxAmount') ?? stageValue('milestoneGSTMinor', 'milestoneGST')) : undefined;
  const stageGrandOverride = useStageOverrides ? (stageValue('milestoneTotalMinor', 'amountDue') ?? stageValue('amountDueMinor', 'amountDue') ?? stageValue('milestoneTotalMinor', 'milestoneTotal')) : undefined;
  const sourceItemsTaxable = rawItems.reduce((sum: number, it: any) => {
    const line = lineGross(it.quantity ?? it.qty ?? 1, it.rate ?? it.unitPrice ?? 0, it.unit);
    return sum + Math.max(0, line * (1 - Number(it.discountPercent ?? it.discount ?? it.disc ?? 0) / 100));
  }, 0);

  const stagePct = data.stagePercent ?? (activeMilestone ? Number((activeMilestone as any).percent ?? (activeMilestone as any).percentage ?? 0) : undefined);
  const targetStageTaxable = stageTaxableOverride ?? (data.stageAmountsAreFinal ? (data.taxableAmount ?? data.subtotal) : undefined);
  const stageScale = stagePct && stagePct > 0 && stagePct < 100 ? (stagePct / 100) : 1;

  let itemStageScale = 1;
  if (data.stageAmountsAreFinal) {
    itemStageScale = 1;
  } else if (targetStageTaxable !== undefined && sourceItemsTaxable > 0 && Math.abs(sourceItemsTaxable - targetStageTaxable) > 1) {
    itemStageScale = targetStageTaxable / sourceItemsTaxable;
  } else if (stagePct && stagePct > 0 && stagePct < 100) {
    if (targetStageTaxable !== undefined && sourceItemsTaxable > 0 && Math.abs(sourceItemsTaxable - targetStageTaxable) <= 1) {
      itemStageScale = 1;
    } else if (sourceItemsTaxable > 0) {
      itemStageScale = stagePct / 100;
    }
  }

  const isGstActive = data.gstMode !== 'none';
  const gstCols = isGstActive;

  const isPlaceholderLine = (item: any) => {
    if (!item) return false;
    const str = String(item.name || item.description || '').toLowerCase();
    return str.includes('full 100% payment on delivery') || str.includes('payment on delivery / order for quotation');
  };
  const filteredRawItems = rawItems.filter((it: any) => !isPlaceholderLine(it));
  const effectiveRawItems = filteredRawItems.length > 0 ? filteredRawItems : rawItems;

  // SKU lookup for the optional SKU column: match the line's catalog item first, then its name.
  const skuForItem = (it: any) => {
    const id = it?.catalogItemId !== undefined && it?.catalogItemId !== null ? String(it.catalogItemId) : '';
    const name = String(it?.name || it?.description || '').trim().toLowerCase();
    const byId = id ? productsList.find((p: any) => String(p._id || p.id || '') === id) : undefined;
    const byName = name ? productsList.find((p: any) => String(p.name || '').trim().toLowerCase() === name) : undefined;
    return (byId as any)?.sku || (byName as any)?.sku || (it?.sku ?? '');
  };
  const hasAnyItemImage = effectiveRawItems.some((it: any) => Boolean(it.image));

  const itemsList = effectiveRawItems.length > 0 ? effectiveRawItems.map((it: any) => {
    const rawRate = Number(it.rate ?? it.unitPrice ?? 0);
    const effectiveRate = itemStageScale !== 1 ? Math.round(rawRate * itemStageScale * 100) / 100 : rawRate;
    const name = it.name || it.description || it.item || 'Deliverable / service';
    // Only the rep's note prints under the name, never the service's master-data description.
    let subDesc: string | undefined = printedLineNote(it) || undefined;
    if (!subDesc) {
      if (itemStageScale !== 1) {
        subDesc = `(${data.stagePercent}% milestone share)`;
      }
    }
    return {
      description: name,
      subDescription: subDesc,
      hsn: it.hsn || it.hsnSac || '998314',
      qty: Number(it.qty ?? it.quantity ?? 1),
      unit: it.unit || 'Nos',
      rate: effectiveRate,
      disc: Number(it.disc ?? it.discount ?? it.discountPercent ?? 0),
      gst: isGstActive ? Number(it.gst ?? it.gstRate ?? 18) : 0,
      image: it.image || '',
      sku: it.sku || skuForItem(it),
    };
  }) : defaultItems.map(it => ({
    ...it,
    rate: itemStageScale !== 1 ? Math.round(it.rate * itemStageScale * 100) / 100 : it.rate,
    subDescription: it.subDescription + (itemStageScale !== 1 ? `\n• Milestone share: ${data.stagePercent}%` : ''),
    gst: isGstActive ? (it.gst ?? 18) : 0,
  }));

  let gross = 0;
  let totalDiscount = 0;
  let taxableTotal = 0;
  let taxTotal = 0;
  const slabs: Record<number, number> = {};

  // Column layout of the items table is driven by the display options; the embedded
  // editor script reads the same flags from the table's dataset.
  const thumbCol = optShowThumbs && hasAnyItemImage;
  const itemsColsBeforeTaxable = (optShowSerial ? 1 : 0) + (thumbCol ? 1 : 0) + (optShowSku ? 1 : 0) + 1 + (optShowHsn ? 1 : 0) + 4;
  const itemsTotalCols = itemsColsBeforeTaxable + (gstCols ? 2 : 0) + ((isProforma && isGstActive) ? 1 : 0);
  const rowdelBtn = '<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();renumber();recalc()">×</button>';

  const itemsRowsHtml = itemsList.map((it: any, i: number) => {
    const line = lineGross(it.qty, it.rate, it.unit);
    const d = line * (it.disc / 100);
    const tv = line - d;
    const itemGst = isGstActive ? (Number(it.gst ?? it.gstRate ?? 18) || 0) : 0;
    const t = isGstActive ? tv * (itemGst / 100) : 0;

    gross += line;
    totalDiscount += d;
    taxableTotal += tv;
    taxTotal += t;

    if (t > 0 && isGstActive) slabs[itemGst] = (slabs[itemGst] || 0) + t;

    const tvStr = '₹' + tv.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const tStr  = '₹' + t.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const lineTotal = isProforma && isGstActive ? tv + t : 0;
    const lineTotalStr = isProforma && isGstActive ? '₹' + lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

    const gstRowExtra = gstCols ? `<td class="n" contenteditable>${itemGst}</td><td class="n calc" data-tax>${tStr}</td>` : '';
    const totalColExtra = isProforma && isGstActive ? `<td class="n calc" data-linetotal>${lineTotalStr}</td>` : '';

    const serialCell = optShowSerial ? `<td class="c" data-f="serial">${i + 1}${rowdelBtn}</td>` : '';
    const thumbCell = thumbCol
      ? `<td class="thumbcol c">${it.image ? `<img src="${escapeAttr(it.image)}" alt=""/>` : ''}</td>`
      : '';
    const skuCell = optShowSku ? `<td class="c" contenteditable data-ph="—">${escapeAttr(String(it.sku || ''))}</td>` : '';
    const descInner = `<div class="it-name" contenteditable>${it.description}</div>${
      !optDescFullWidth && it.subDescription ? `<div class="it-desc" contenteditable>${String(it.subDescription).replace(/\n/g, '<br/>')}</div>` : ''
    }`;
    const descCell = `<td class="desccol" data-f="desc">${optShowSerial ? '' : rowdelBtn}${descInner}</td>`;
    const hsnCell = optShowHsn ? `<td class="hsncol c" contenteditable>${it.hsn}</td>` : '';
    const subRow = (optDescFullWidth && it.subDescription)
      ? `\n<tr data-subrow="1"><td colspan="${itemsTotalCols}"><div class="it-desc" contenteditable>${String(it.subDescription).replace(/\n/g, '<br/>')}</div></td></tr>`
      : '';

    return `
      <tr data-item-row="1">${serialCell}${thumbCell}${skuCell}${descCell}${hsnCell}
        <td class="n" contenteditable data-f="qty">${it.qty}</td>
        <td class="c" contenteditable data-f="unit">${unitLabel(it.unit)}</td>
        <td class="n" contenteditable data-f="rate">${it.rate}</td>
        <td class="n" contenteditable data-f="disc">${it.disc}</td>
        <td class="n calc" data-taxable>${tvStr}</td>${gstRowExtra}${totalColExtra}
      </tr>${subRow}
    `;
  }).join('');

  if (data.headerDiscount && Number(data.headerDiscount) > 0) {
    let headerDisc = 0;
    if (data.headerDiscountType === 'percent') {
      headerDisc = (gross - totalDiscount) * (Number(data.headerDiscount) / 100);
    } else {
      headerDisc = Number(data.headerDiscount) * itemStageScale;
    }
    totalDiscount += headerDisc;
    taxableTotal = Math.max(0, gross - totalDiscount);
    const taxScale = gross > 0 ? taxableTotal / (gross - (totalDiscount - headerDisc) || 1) : 1;
    taxTotal = isGstActive ? Math.round(taxTotal * taxScale * 100) / 100 : 0;
  }

  if (!isGstActive) {
    taxTotal = 0;
  }

  if (stageTaxableOverride !== undefined) taxableTotal = stageTaxableOverride;
  else if (data.stageAmountsAreFinal && data.subtotal !== undefined && Number(data.subtotal) > 0) taxableTotal = Number(data.subtotal);
  if (!isGstActive) taxTotal = 0;
  else if (stageTaxOverride !== undefined) taxTotal = stageTaxOverride;
  else if (data.stageAmountsAreFinal && data.taxTotal !== undefined && Number(data.taxTotal) > 0) taxTotal = Number(data.taxTotal);

  const taxableStr  = '₹' + taxableTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const taxTotalStr = '₹' + taxTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Additional charges are a payable row in the totals block — include them in the grand
  // total so the rendered amount matches the quotation form and the stored document total.
  const additionalChargesAmt = Number(data.additionalCharges || 0) || 0;
  const preRound   = taxableTotal + (isGstActive ? taxTotal : 0) + (additionalChargesAmt > 0 ? additionalChargesAmt : 0);
  const roundOff   = Math.round(preRound) - preRound;
  const grandTotal = stageGrandOverride !== undefined
    ? stageGrandOverride
    : (data.stageAmountsAreFinal && (data.grandTotal !== undefined || data.total !== undefined))
      ? Number(data.grandTotal ?? data.total)
      : Math.round((preRound + roundOff) * 100) / 100;

  const curSymbol = data.currency || '₹';
  const fmtCurrency = (n: number) => curSymbol + (curSymbol.length > 1 ? ' ' : '') + Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const gstSlabsHtml = Object.keys(slabs).sort((a, b) => Number(a) - Number(b)).map(rt => {
    const r = Number(rt);
    if (data.gstMode === 'inter') {
      return `<div class="r"><span>IGST @ ${r}%</span><span>${fmtCurrency(slabs[r])}</span></div>`;
    } else if (data.gstMode !== 'none') {
      const half = slabs[r] / 2;
      return `<div class="r"><span>CGST @ ${r / 2}%</span><span>${fmtCurrency(half)}</span></div>
              <div class="r"><span>SGST @ ${r / 2}%</span><span>${fmtCurrency(slabs[r] - half)}</span></div>`;
    }
    return '';
  }).filter(Boolean).join('');

  const totalsHtml = `
    <div class="r"><span>Gross value</span><span>${fmtCurrency(gross)}</span></div>
    ${totalDiscount > 0 ? `<div class="r disc"><span>Discount</span><span>− ${fmtCurrency(totalDiscount)}</span></div>` : ''}
    ${(data.additionalCharges && Number(data.additionalCharges) > 0) ? `<div class="r"><span>${(data as any).additionalChargesLabel || 'Additional charges'}</span><span>+ ${fmtCurrency(Number(data.additionalCharges))}</span></div>` : ''}
    <div class="r"><span>Total value</span><span>${taxableStr}</span></div>
    ${optShowTaxSummary ? gstSlabsHtml : ''}
    ${Math.abs(roundOff) >= 0.005 ? `<div class="r"><span>Round off</span><span>${roundOff < 0 ? '− ' : '+ '}${fmtCurrency(Math.abs(roundOff))}</span></div>` : ''}
    <div class="r grand"><span>${isProforma ? 'Total payable' : 'Grand Total'}</span><span>${fmtCurrency(grandTotal)}</span></div>
  `;

  const amtReceived = Number(data.amountReceived || 0);
  const tdsRate     = Number(data.tdsRate || 0);
  const tdsAmt      = tdsRate > 0 ? Math.round((taxableTotal * (tdsRate / 100)) * 100) / 100 : 0;
  const otherStagesDue = isRunningBill ? Math.max(0, Number(data.otherStagesDue) || 0) : 0;
  const balanceDue  = Math.round((grandTotal - amtReceived - tdsAmt + otherStagesDue) * 100) / 100;

  const tdsAmtStr  = '− ' + fmtCurrency(tdsAmt);
  const balanceStr = fmtCurrency(balanceDue);

  const notesList = Array.isArray(data.notes)
    ? data.notes
    : (typeof data.notes === 'string' && data.notes.trim())
    ? data.notes.split('\n').map(s => s.trim()).filter(Boolean)
    : [];
  const notesHtml = notesList.map(n => `<li contenteditable>${n}</li>`).join('\n');

  const rawTerms = (Array.isArray((data as any).termsList) && (data as any).termsList.length > 0)
    ? (data as any).termsList
    : (Array.isArray(data.terms)
      ? data.terms
      : (typeof data.terms === 'string' && data.terms.trim() ? data.terms.split('\n') : null));

  const termsList = rawTerms !== null
    ? rawTerms.map((s: any) => String(s || '').trim()).filter(Boolean)
    : [];
  const termsHtml = termsList.map((t: string) => `<li contenteditable>${t}</li>`).join('\n');

  const rawAssumptions = (Array.isArray((data as any).assumptionsList) && (data as any).assumptionsList.length > 0)
    ? (data as any).assumptionsList
    : (Array.isArray(data.assumptions)
      ? data.assumptions
      : (typeof data.assumptions === 'string' && data.assumptions.trim() ? data.assumptions.split('\n') : null));

  const assumptionsList = rawAssumptions !== null
    ? rawAssumptions.map((s: any) => String(s || '').trim()).filter(Boolean)
    : [];
  const assumptionsHtml = assumptionsList.map((a: string) => `<li contenteditable>${a}</li>`).join('\n');

  /* Payment Milestones & Stage Payment Schedule */
  const fallbackStageName = data.milestoneName ||
    data.milestonePhaseLabel ||
    data.stageName ||
    ((data.items && data.items.length === 1 && data.items[0].name && /Stage/i.test(data.items[0].name)) ? data.items[0].name : '') ||
    (isTax ? 'Stage 1: Advance / Mobilization' : 'Full Payment (100%)');

  const isMilestone = (data.paymentType === 'milestone_based' || (Array.isArray(data.milestones) && data.milestones.length > 0)) && data.paymentType !== 'full_payment';
  const showInvoiceSchedule = !isFinalInvoice && (isProforma || isTax || (isQuotation && isMilestone));
  const rawMilestones = Array.isArray(data.milestones) ? data.milestones : [];
  const defaultMilestones = [
    { name: isTax ? 'Stage 1: Advance / Mobilization' : 'Advance on order confirmation', percent: 25, due: 'On signing', status: (data.amountReceived || 0) > 0 ? 'paid' : 'pending', invoiceNumber: data.invoiceNumber || '' },
    { name: isTax ? 'Stage 2: Final Delivery / Handover' : 'Phase 1 Completion', percent: 25, due: 'T + 15 days', status: 'pending', invoiceNumber: '' },
    { name: 'Phase 2 Completion', percent: 25, due: 'T + 30 days', status: 'pending', invoiceNumber: '' },
    { name: 'Final Delivery', percent: 25, due: 'T + 45 days', status: 'pending', invoiceNumber: '' },
  ];
  const fullPaymentMilestone = {
    name: fallbackStageName,
    percent: data.stagePercent || 100,
    due: data.paymentTerms || 'Due on receipt',
    status: 'pending',
    invoiceNumber: data.invoiceNumber || '',
    proformaNumber: data.proformaNumber || (isProforma ? data.quotationNumber || '' : ''),
    netAmount: taxableTotal,
    taxAmount: taxTotal,
    amountDue: grandTotal,
  };
  const milestonesList = isMilestone
    ? (rawMilestones.length > 0 ? rawMilestones : defaultMilestones)
    : (showInvoiceSchedule ? [fullPaymentMilestone] : []);
  const fmtMoney = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const showStatusCols = false;
  // A tax invoice / running bill's schedule also says when each stage's payment was received.
  const showPaidOn = isTax;
  const fmtDay = (v: unknown) => {
    const d = v ? new Date(String(v)) : null;
    return d && !isNaN(d.getTime()) ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  };

  // Unscaled full contract values for the complete milestone schedule breakdown.
  const scheduleStageScale = (data.stagePercent && data.stagePercent > 0 && data.stagePercent < 100)
    ? (data.stagePercent / 100)
    : 1;

  const passedContractTaxable = Number(data.contractTaxable || 0);
  const passedContractGrand   = Number(data.contractGrand   || (data as any).contractTotal || 0);
  const passedContractTax     = Number(data.contractTax     || 0);

  // The milestone schedule table ALWAYS represents the FULL project contract (100%).
  // If this document is for a fractional stage (scheduleStageScale < 1), unscale to get full contract totals.
  // Otherwise, the particulars ARE the full contract totals.
  const unscaledDocTaxable = (scheduleStageScale > 0 && scheduleStageScale < 1 && taxableTotal > 0)
    ? Math.round((taxableTotal / scheduleStageScale) * 100) / 100
    : taxableTotal;
  const unscaledDocTax = (scheduleStageScale > 0 && scheduleStageScale < 1 && taxTotal > 0)
    ? Math.round((taxTotal / scheduleStageScale) * 100) / 100
    : taxTotal;
  const unscaledDocGrand = (scheduleStageScale > 0 && scheduleStageScale < 1 && grandTotal > 0)
    ? Math.round((grandTotal / scheduleStageScale) * 100) / 100
    : grandTotal;

  const sumMsTaxable = milestonesList.reduce((s: number, m: any) =>
    s + (Number(m.netAmount || m.milestoneTaxableAmount || (m.milestoneTaxableAmountMinor ? m.milestoneTaxableAmountMinor / 100 : 0)) || 0), 0);
  const sumMsTax = milestonesList.reduce((s: number, m: any) =>
    s + (Number(m.taxAmount || m.milestoneGST || (m.milestoneGSTMinor ? m.milestoneGSTMinor / 100 : 0)) || 0), 0);
  const sumMsGrand = milestonesList.reduce((s: number, m: any) =>
    s + (Number(m.amountDue || m.amount || m.milestoneTotal || (m.milestoneTotalMinor ? m.milestoneTotalMinor / 100 : 0)) || 0), 0);

  const hasHeaderDiscount = Boolean(data.headerDiscount && Number(data.headerDiscount) > 0);

  const fullContractTaxable: number = (passedContractTaxable > 0 && !(hasHeaderDiscount && passedContractTaxable > unscaledDocTaxable))
    ? passedContractTaxable
    : (unscaledDocTaxable > 0
      ? unscaledDocTaxable
      : (sumMsTaxable > 0 ? sumMsTaxable : taxableTotal));

  const fullContractTax: number = (passedContractTax > 0 && !(hasHeaderDiscount && passedContractTax > unscaledDocTax))
    ? passedContractTax
    : (unscaledDocTax > 0
      ? unscaledDocTax
      : (sumMsTax > 0 ? sumMsTax : (isGstActive && taxTotal > 0 ? taxTotal : (isGstActive ? Math.round(fullContractTaxable * 0.18 * 100) / 100 : 0))));

  const fullContractGrand: number = (passedContractGrand > 0 && !(hasHeaderDiscount && passedContractGrand > unscaledDocGrand))
    ? passedContractGrand
    : (unscaledDocGrand > 0
      ? unscaledDocGrand
      : (sumMsGrand > 0 ? sumMsGrand : (grandTotal > 0 ? grandTotal : Math.round((fullContractTaxable + fullContractTax) * 100) / 100)));

  let totMilestoneTaxable = 0, totMilestoneCgst = 0, totMilestoneSgst = 0, totMilestoneTax = 0, totMilestoneGrand = 0, totMilestonePct = 0;

  const activeMilestoneIdx = (data.milestoneIndex !== undefined && data.milestoneIndex !== null)
    ? Number(data.milestoneIndex)
    : ((data.stageIndex !== undefined && data.stageIndex !== null) ? Number(data.stageIndex) : null);

  const milestonesRowsHtml = milestonesList.map((m: any, i) => {
    const isCurrent = activeMilestoneIdx !== null && i === activeMilestoneIdx;
    const mPct = Number(m.percent ?? m.percentage ?? m.pct ?? (milestonesList.length > 0 ? (100 / milestonesList.length) : 25));
    totMilestonePct += mPct;

    const toVal = (valKey: string, minorKey: string) => {
      if (m[valKey] !== undefined && m[valKey] !== null && Number(m[valKey]) > 0) return Number(m[valKey]);
      if (m[minorKey] !== undefined && m[minorKey] !== null && Number(m[minorKey]) > 0) return Number(m[minorKey]) / 100;
      return 0;
    };

    let mTaxable: number;
    let mCgst: number;
    let mSgst: number;
    let mTax: number;
    let mAmt: number;

    if (m.exact === true) {
      // Amounts already worked out per stage by the server (they add up to the contract to the paisa).
      mTaxable = toVal('netAmount', 'milestoneTaxableAmountMinor');
      mCgst    = chargesNoTax ? 0 : toVal('cgstAmount', 'milestoneCGSTMinor');
      mSgst    = chargesNoTax ? 0 : toVal('sgstAmount', 'milestoneSGSTMinor');
      mTax     = chargesNoTax ? 0 : (toVal('taxAmount', 'milestoneGSTMinor') || (mCgst + mSgst));
      mAmt     = toVal('amountDue', 'amountDueMinor');
      if (chargesNoTax) {
        if (mAmt === 0 && mTaxable > 0) mAmt = mTaxable;
        if (mTaxable === 0 && mAmt > 0) mTaxable = mAmt;
      } else if (mCgst === 0 && mSgst === 0 && mTax > 0) {
        mCgst = Math.round((mTax / 2) * 100) / 100;
        mSgst = Math.round((mTax - mCgst) * 100) / 100;
      }
    } else if (isCurrent && taxableTotal > 0) {
      // Active stage — use the document's own calculated amounts
      mTaxable = taxableTotal;
      mTax     = chargesNoTax ? 0 : taxTotal;
      mCgst    = chargesNoTax ? 0 : taxTotal / 2;
      mSgst    = chargesNoTax ? 0 : taxTotal - (taxTotal / 2);
      mAmt     = chargesNoTax ? taxableTotal : grandTotal;
    } else if (fullContractGrand > 0 && mPct > 0) {
      // Always recalculate proportionally from the proforma's discounted contract totals
      // so that any discount change is instantly reflected across all milestone rows.
      mAmt     = Math.round(fullContractGrand    * (mPct / 100) * 100) / 100;
      const base = chargesNoTax ? fullContractGrand : (fullContractTaxable > 0 ? fullContractTaxable : (fullContractGrand / 1.18));
      mTaxable = chargesNoTax ? mAmt : Math.round(base * (mPct / 100) * 100) / 100;
      const taxBase = chargesNoTax ? 0 : (fullContractTax > 0 ? fullContractTax : (mTaxable * 0.18));
      mTax     = chargesNoTax ? 0 : Math.round(taxBase * (mPct / 100) * 100) / 100;
      mCgst    = chargesNoTax ? 0 : Math.round((mTax / 2) * 100) / 100;
      mSgst    = chargesNoTax ? 0 : Math.round((mTax - mCgst) * 100) / 100;
    } else {
      // Fallback: read stored milestone fields only when no contract total is available
      mTaxable = toVal('netAmount', 'milestoneTaxableAmountMinor') || toVal('milestoneTaxableAmount', 'milestoneTaxableAmountMinor') || toVal('taxable', 'taxableMinor');
      mCgst    = chargesNoTax ? 0 : (toVal('cgstAmount', 'milestoneCGSTMinor') || toVal('milestoneCGST', 'milestoneCGSTMinor'));
      mSgst    = chargesNoTax ? 0 : (toVal('sgstAmount', 'milestoneSGSTMinor') || toVal('milestoneSGST', 'milestoneSGSTMinor'));
      mTax     = chargesNoTax ? 0 : (toVal('taxAmount', 'milestoneGSTMinor') || toVal('milestoneGST', 'milestoneGSTMinor') || (mCgst + mSgst));
      mAmt     = toVal('amountDue', 'amountDueMinor') || toVal('amount', 'milestoneTotalMinor') || toVal('milestoneTotal', 'milestoneTotalMinor');
      if (chargesNoTax) {
        if (mAmt === 0 && mTaxable > 0) mAmt = mTaxable;
        if (mTaxable === 0 && mAmt > 0) mTaxable = mAmt;
      } else {
        if (mCgst === 0 && mSgst === 0 && mTax > 0) { mCgst = mTax / 2; mSgst = mTax - mCgst; }
        if (mTaxable === 0 && mAmt > 0) mTaxable = mAmt / 1.18;
        if (mTax === 0 && mTaxable > 0) mTax = mTaxable * 0.18;
        if (mAmt === 0 && mTaxable > 0) mAmt = mTaxable + mTax;
      }
    }


    const mEffectiveTaxRate = (mTaxable > 0 && mTax > 0) ? Math.round((mTax / mTaxable) * 100) : (chargesNoTax ? 0 : 18);
    const mCgstRatePct = Number(m.cgstRate) > 0 ? Number(m.cgstRate) : Math.round((mEffectiveTaxRate / 2) * 100) / 100;
    const mIgstRatePct = Number(m.igstRate) > 0 ? Number(m.igstRate) : Math.round(mCgstRatePct * 2 * 100) / 100;

    totMilestoneTaxable += mTaxable;
    totMilestoneCgst += mCgst;
    totMilestoneSgst += mSgst;
    totMilestoneTax += mTax;
    totMilestoneGrand += mAmt;

    let rawName = m.name || m.label || `Stage ${i + 1}`;
    if (isTax && /full\s*(?:time\s*)?payment/i.test(rawName)) {
      rawName = data.milestoneName || data.milestonePhaseLabel || data.stageName || (data.milestones?.[i]?.name) || (i === 0 ? 'Stage 1: Advance / Mobilization' : `Stage ${i + 1}`);
    }
    const cleanName = rawName.replace(/\s*\(Current Phase\)/gi, '').trim();
    const mName = isCurrent ? `${cleanName} <b>(Current Phase)</b>` : cleanName;
    // Per-stage document numbers in the milestone payment section: the proforma number
    // issued for the stage (on a proforma, the document's own number) and, once issued,
    // the stage's tax invoice number — both in the master-data numbering format.
    const stageProformaNo = String(m.proformaNumber || (isProforma ? dno : '') || '').trim();
    const stageInvoiceNo = String(m.invoiceNumber || (isCurrent ? dno : (!isMilestone && isTax ? dno : '')) || '').trim();
    const hasPi = Boolean(stageProformaNo);
    const hasRbOrTi = Boolean(stageInvoiceNo && stageInvoiceNo !== stageProformaNo);
    const invPrefix = isRunningBill ? 'RB' : chargesNoTax ? 'EI' : 'TI';

    const mDue = m.due || (m.dueDate ? (String(m.dueDate).includes('T') ? String(m.dueDate).split('T')[0] : String(m.dueDate)) : '—');
    const isPaid = m.status === 'paid' || ((m.amountReceived || m.amountReceivedMinor) && (m.amountReceived || m.amountReceivedMinor / 100) >= mAmt);
    const isPartiallyPaid = m.status === 'partially_paid' || ((m.amountReceived || m.amountReceivedMinor) && (m.amountReceived || m.amountReceivedMinor / 100) > 0 && !isPaid);
    const isInvGen = Boolean(m.invoiceNumber || m.status === 'invoice_generated' || isPaid || isPartiallyPaid);
    const invLabel = isInvGen ? (m.invoiceNumber || 'Generated') : 'Pending';
    const payLabel = isPaid ? 'Paid' : isPartiallyPaid ? 'Partially Paid' : 'Unpaid';

    const mDocNo = (isProforma || isTax) && (hasPi || hasRbOrTi || isRunningBill)
      ? `<div style="font-size:8.5px;margin-top:2.5px;line-height:1.35">
          ${hasPi ? `<div style="color:#6b7280;font-weight:500">PI: ${stageProformaNo}</div>` : ''}
          ${hasRbOrTi ? `<div style="color:#6b7280;font-weight:500">${invPrefix}: ${stageInvoiceNo}${isRunningBill ? ` &nbsp;<span style="color:${isPaid ? '#047857' : isPartiallyPaid ? '#1d4ed8' : '#b45309'};font-weight:700">${isInvGen ? '' : 'Not yet billed · '}${payLabel}</span>` : ''}</div>` : (isRunningBill ? `<div style="color:${isPaid ? '#047857' : isPartiallyPaid ? '#1d4ed8' : '#b45309'};font-weight:700">${isInvGen ? '' : 'Not yet billed · '}${payLabel}</div>` : '')}
        </div>`
      : '';

    return `<tr ${isCurrent ? 'data-is-current="1" style="background:#eef2fb;font-weight:600"' : ''}>
      <td class="c">${i + 1}<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();renumberSched();recalc()">×</button></td>
      <td class="desccol" style="text-align:left" contenteditable>${mName}${mDocNo}</td>
      <td class="c" contenteditable>${mPct}%</td>
      <td class="n calc" data-mstaxable>${fmtMoney(mTaxable)}</td>
      <td class="n calc col-intra" data-mscgst>${fmtMoney(mCgst)}<div style="font-size:8px;color:#6b7280;line-height:1.1;margin-top:1px">(${mCgstRatePct}%)</div></td>
      <td class="n calc col-intra" data-mssgst>${fmtMoney(mSgst)}<div style="font-size:8px;color:#6b7280;line-height:1.1;margin-top:1px">(${mCgstRatePct}%)</div></td>
      <td class="n calc col-inter" data-msigst>${fmtMoney(mTax)}<div style="font-size:8px;color:#6b7280;line-height:1.1;margin-top:1px">(${mIgstRatePct}%)</div></td>
      <td class="n calc col-intra" data-mstax>${fmtMoney(mTax)}</td>
      <td class="n calc" data-msamt>${fmtMoney(mAmt)}</td>
      ${showStatusCols ? `<td class="c"><span style="background:${isInvGen ? '#f3e8ff;color:#6b21a8;border:1px solid #d8b4fe' : '#f3f4f6;color:#4b5563;border:1px solid #e5e7eb'};font-weight:700;font-size:9px;padding:2px 6px;border-radius:4px;display:inline-block">${invLabel}</span></td>` : ''}
      ${showStatusCols ? `<td class="c"><span style="background:${isPaid ? '#d1fae5;color:#065f46;border:1px solid #a7f3d0' : isPartiallyPaid ? '#dbeafe;color:#1e40af;border:1px solid #bfdbfe' : '#fef3c7;color:#92400e;border:1px solid #fde68a'};font-weight:700;font-size:9px;padding:2px 6px;border-radius:4px;display:inline-block">${payLabel}</span></td>` : ''}
      <td class="c" contenteditable>${mDue}</td>
      ${showPaidOn ? `<td class="c">${fmtDay(m.paidAt)}</td>` : ''}
    </tr>`;
  }).join('');

  const finalBills = isFinalInvoice ? (data.finalInvoice!.bills || []) : [];
  const finalBillsHtml = isFinalInvoice ? `
  <div data-opt="sched" id="schedOptSection">
    <div class="sec" style="margin-top:14px"><h2 style="margin:0">Running Bills Adjusted Against This Invoice</h2></div>
    <table class="msched">
      <thead><tr>
        <th class="c">#</th>
        <th>Running Bill / Stage</th>
        <th class="n">Taxable (INR)</th>
        <th class="n">GST</th>
        <th class="n">Amount (Incl. GST)</th>
        <th class="n">Received</th>
        <th class="n">Received On</th>
      </tr></thead>
      <tbody>${finalBills.map((b, i) => `<tr>
        <td class="c">${i + 1}</td>
        <td>${b.stageName}<small style="color:#6b7280;font-size:9px;margin-left:4px;font-weight:600">RB: ${b.number}</small></td>
        <td class="n">${fmtMoney(b.taxable)}</td>
        <td class="n">${fmtMoney(b.tax)}</td>
        <td class="n">${fmtMoney(b.total)}</td>
        <td class="n">${fmtMoney(Number(b.received ?? b.total))}</td>
        <td class="n">${fmtDay(b.paidAt)}</td>
      </tr>`).join('')}</tbody>
      <tfoot><tr style="background:#f3f4f6;font-weight:700">
        <td colspan="2" class="n">Total adjusted</td>
        <td class="n">${fmtMoney(finalBills.reduce((s, b) => s + b.taxable, 0))}</td>
        <td class="n">${fmtMoney(finalBills.reduce((s, b) => s + b.tax, 0))}</td>
        <td class="n" style="color:#1e3a8a">${fmtMoney(finalBills.reduce((s, b) => s + b.total, 0))}</td>
        <td class="n">${fmtMoney(finalBills.reduce((s, b) => s + Number(b.received ?? b.total), 0))}</td>
        <td></td>
      </tr></tfoot>
    </table>
    <p style="margin:6px 0 0;font-size:10px;color:#6b7280">Tax on this contract has been charged on the running bills above. This invoice records the completed contract and does not charge GST again.</p>
  </div>` : '';

  const milestonesSectionHtml = showInvoiceSchedule ? `
  <div data-opt="sched" id="schedOptSection">
    <div class="sec" style="margin-top:14px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:6px">
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <h2 style="margin:0">${chargesNoTax ? 'Payment Schedule' : 'Payment Schedule &amp; Tax Division (Milestone Based)'}</h2>
        ${isProforma || isTax ? `<span style="font-size:11px;color:#6b7280;font-weight:600;background:#f3f4f6;border:1px solid #e5e7eb;border-radius:4px;padding:2px 8px">${dno}</span>` : ''}
      </div>
    </div>
    <div class="table-wrap" style="width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;margin:10px 0">
    <table class="msched">
      <colgroup>
        ${showStatusCols 
          ? '<col style="width:22px"/><col/><col style="width:38px"/><col style="width:68px"/><col class="col-intra" style="width:58px"/><col class="col-intra" style="width:58px"/><col class="col-inter" style="width:66px"/><col class="col-intra" style="width:58px"/><col style="width:72px"/><col style="width:58px"/><col style="width:58px"/><col style="width:62px"/>' 
          : (showPaidOn 
              ? '<col style="width:22px"/><col/><col style="width:42px"/><col style="width:72px"/><col class="col-intra" style="width:62px"/><col class="col-intra" style="width:62px"/><col class="col-inter" style="width:70px"/><col class="col-intra" style="width:62px"/><col style="width:76px"/><col style="width:66px"/><col style="width:66px"/>' 
              : '<col style="width:24px"/><col/><col style="width:48px"/><col style="width:78px"/><col class="col-intra" style="width:66px"/><col class="col-intra" style="width:66px"/><col class="col-inter" style="width:74px"/><col class="col-intra" style="width:66px"/><col style="width:82px"/><col style="width:72px"/>'
            )
        }
      </colgroup>
      <thead><tr>
        <th class="c">#</th>
        <th class="desccol" style="text-align:left">Milestone / Stage</th>
        <th class="c">% Total</th>
        <th class="n">Taxable (INR)</th>
        <th class="n col-intra">CGST</th>
        <th class="n col-intra">SGST</th>
        <th class="n col-inter">IGST</th>
        <th class="n col-intra">Total Tax</th>
        <th class="n" style="line-height:1.2">Amount<br><span style="font-size:7.5px;opacity:0.9">${chargesNoTax ? '' : '(Incl. GST)'}</span></th>
        ${showStatusCols ? '<th class="c">Invoice Status</th><th class="c">Payment Status</th>' : ''}
        <th class="c">Due / Trigger</th>
        ${showPaidOn ? '<th class="c">Received On</th>' : ''}
      </tr></thead>
      <tbody id="schedBody">${milestonesRowsHtml}</tbody>
      <tfoot>
        <tr style="background:#f3f4f6;font-weight:700">
          <td colspan="2" class="n">Schedule Total</td>
          <td class="n" id="msTotalPct">${totMilestonePct}%</td>
          <td class="n" id="msTotalTaxable">${fmtMoney(totMilestoneTaxable)}</td>
          <td class="n col-intra" id="msTotalCgst">${fmtMoney(totMilestoneCgst)}</td>
          <td class="n col-intra" id="msTotalSgst">${fmtMoney(totMilestoneSgst)}</td>
          <td class="n col-inter" id="msTotalIgst">${fmtMoney(totMilestoneTax)}</td>
          <td class="n col-intra" id="msTotalTax">${fmtMoney(totMilestoneTax)}</td>
          <td class="n" id="msTotalGrand" style="color:#1e3a8a">${fmtMoney(totMilestoneGrand)}</td>
          ${showStatusCols ? '<td></td><td></td>' : ''}
          <td></td>
          ${showPaidOn ? '<td></td>' : ''}
        </tr>
      </tfoot>
    </table>
    </div>
  </div>` : '';

  const bankAccName = data.bankAccName || '';
  const bankAccNo   = data.bankAccNo   || '';
  const bankName    = data.bankName    || '';
  const bankBranch  = data.bankBranch  || '';
  const bankIfsc    = data.bankIfsc    || '';
  const bankUpi     = data.bankUpi     || '';

  const declaration = data.declaration || '';
  // The signature image comes from the company profile (master data); the printed
  // name defaults so the signature line is never blank on any document type.
  const signatory   = data.signatory   || 'Authorised Signatory';
  const signatoryRole = data.signatoryRole || '';
  const signatureUrl = data.signatureUrl || (data as any).signature || '';

  const finalHtml = `<!doctype html>
<html lang="en" data-doc="${docTypeVal}" data-gst="${data.gstMode || 'none'}" data-stage-scale="${data.stagePercent && data.stagePercent > 0 && data.stagePercent < 100 ? data.stagePercent / 100 : 1}" data-stage-amounts-final="${data.stageAmountsAreFinal ? '1' : '0'}" data-contract-taxable="${fullContractTaxable}" data-contract-grand="${fullContractGrand}" data-contract-tax="${fullContractTax}">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${docTitle} ${dno} — ${coName}</title>
<style>
:root{
  --accent:#1e3a8a; --accent-soft:#eef2fb;
  --ink:#111827; --muted:#6b7280; --line:#d8dbe2; --radius:6px;
}
*{box-sizing:border-box;margin:0;padding:0}
/* CGST + SGST columns for a sale inside the seller's state; one IGST column for a sale to another state. */
html[data-gst="inter"] .col-intra{display:none}
html:not([data-gst="inter"]) .col-inter{display:none}
/* A document that charges no tax shows no tax columns at all. */
html[data-gst="none"] .col-intra{display:none}
body{
  font:12px/1.55 'Segoe UI',Inter,Arial,Helvetica,sans-serif;
  color:var(--ink);background:#eceef1;padding:20px 12px 60px;
  -webkit-print-color-adjust:exact;print-color-adjust:exact;
}

button,select,input[type=color],input[type=text].tb{
  font:inherit;font-size:11.5px;padding:6px 10px;border:1px solid var(--line);
  background:#fff;border-radius:var(--radius);cursor:pointer;color:var(--ink);
}
button:hover{background:var(--accent-soft);border-color:var(--accent)}
button.primary{background:var(--accent);color:#fff;border-color:var(--accent);font-weight:600}
button.primary:hover{filter:brightness(1.12)}
input[type=color]{padding:2px;width:34px;height:28px}
input[type=text].tb{width:52px;cursor:text}
.sheet{
  width:100%;max-width:210mm;min-height:297mm;margin:0 auto;background:#fff;padding:13mm 13mm 11mm;
  box-shadow:0 4px 22px rgba(0,0,0,.12);position:relative;overflow:hidden;box-sizing:border-box;
}
@media screen and (max-width:768px){
  body{padding:10px 4px 40px}
  .sheet{padding:6mm 6mm 6mm;box-shadow:none}
  .hdr{flex-direction:column;gap:10px}
  .doc{text-align:left}
  .info,.strip,.cols,.tot-wrap{flex-direction:column;gap:10px}
  .box.acct,.rightcol,.sign{width:100%;flex:auto}
  table{display:block;overflow-x:auto;white-space:nowrap}
}
.sheet+.sheet{margin-top:20px}
[contenteditable]{outline:0}
[contenteditable]:hover{background:#fffbea}
[contenteditable]:focus{background:#fff7d1;box-shadow:0 0 0 1px #e9c46a}
[data-ph]:empty:before{content:attr(data-ph);color:#b6bac2;font-weight:400}
.wm{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
    font-size:80px;font-weight:800;color:rgba(30,58,138,.055);transform:rotate(-30deg);
    letter-spacing:10px;pointer-events:none;z-index:0;white-space:nowrap}
.body{position:relative;z-index:1}

.hdr{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;
     border-bottom:3px solid var(--accent);padding-bottom:11px}
.brand{display:flex;gap:12px;align-items:flex-start}
.logo-slot{
  width:72px;height:72px;flex:none;border:1px dashed var(--line);border-radius:var(--radius);
  display:flex;align-items:center;justify-content:center;cursor:pointer;overflow:hidden;
  background:#fafbfc;text-align:center;font-size:9px;color:#9aa0aa;line-height:1.25;padding:4px;
}
.logo-slot img{width:100%;height:100%;object-fit:contain}
.logo-slot.has-logo{border-style:solid;border-color:transparent;background:none}
.co-name{font-size:18.5px;font-weight:700;color:var(--accent);letter-spacing:.2px;line-height:1.2}
.co-tag{font-size:10.5px;color:var(--muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.4px}
.co-meta{font-size:10.5px;color:#444;line-height:1.6}
.doc{text-align:right;flex:none}
.doc h1{font-size:21px;letter-spacing:2.5px;text-transform:uppercase;color:var(--accent);font-weight:700;white-space:nowrap}
.doc .sub{font-size:9.6px;color:var(--muted);letter-spacing:1px;text-transform:uppercase;margin-top:2px}
.copylabel{display:inline-block;margin-top:5px;border:1px solid var(--accent);color:var(--accent);
           padding:2px 8px;font-size:9px;font-weight:700;letter-spacing:1px;text-transform:uppercase;border-radius:3px}
.notax{display:inline-block;margin-top:5px;background:#fef3c7;border:1px solid #f59e0b;color:#92400e;
       padding:2px 8px;font-size:9px;font-weight:700;letter-spacing:.6px;border-radius:3px}

.info{display:flex;gap:9px;margin:12px 0 10px;align-items:stretch}
.box{border:1px solid var(--line);border-radius:var(--radius);padding:8px 10px;flex:1;min-width:0}
.box.acct{background:var(--accent-soft);border-color:#c8d3ee}
.box h3{font-size:9.5px;text-transform:uppercase;letter-spacing:.8px;color:var(--accent);font-weight:700;margin-bottom:5px}
.box .nm{font-weight:700;font-size:12.5px}
.box .ln{font-size:10.5px;color:#4b5563;line-height:1.6}
.kv{display:flex;justify-content:space-between;gap:8px;font-size:10.5px;padding:1.5px 0}
.kv span:first-child{color:var(--muted);white-space:nowrap}
.kv span:last-child{font-weight:600;text-align:right}

.sec{display:flex;align-items:center;gap:8px;margin:13px 0 5px}
.sec h2{font-size:10.5px;text-transform:uppercase;letter-spacing:1.1px;color:var(--accent);font-weight:700;white-space:nowrap}
.sec:after{content:'';flex:1;height:1px;background:var(--line)}

.table-wrap{width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;margin:10px 0}
table{width:100%;border-collapse:collapse;table-layout:auto;box-sizing:border-box}
th,td{border:1px solid var(--line);padding:6px 7px;font-size:10.5px;vertical-align:middle;box-sizing:border-box}
th{background:var(--accent);color:#fff;font-weight:600;font-size:9.5px;text-transform:uppercase;
   letter-spacing:.3px;text-align:center;white-space:nowrap;line-height:1.35}
th.desccol,td.desccol{text-align:left;word-break:normal;word-wrap:normal;white-space:normal}
.thumbcol img{max-width:100%;max-height:40px;object-fit:contain;display:inline-block;vertical-align:middle}
th.n,td.n{text-align:right}
th.c,td.c{text-align:center}
td.n,td.c{white-space:nowrap}
tbody tr:nth-child(even) td{background:#fbfcfd}
tfoot td{background:#f3f4f6;font-weight:700;white-space:nowrap}
.it-name{font-weight:600;font-size:11px}
.it-desc{font-size:9.8px;color:var(--muted);line-height:1.5;margin-top:2px;white-space:pre-wrap}
tbody td:first-child{position:relative}
.rowdel{position:absolute;top:50%;left:2px;transform:translateY(-50%);z-index:5;border:0;background:#fee2e2;color:#b91c1c;
        border-radius:4px;width:15px;height:15px;line-height:13px;text-align:center;padding:0;font-size:11px;font-weight:700;
        opacity:0;transition:.15s;box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:pointer}
tr:hover .rowdel{opacity:1}
.btn-add-inline{font-size:9.5px;font-weight:600;padding:2px 8px;background:var(--accent-soft);color:var(--accent);border:1px solid var(--accent);border-radius:4px;cursor:pointer;line-height:1.4;margin-left:auto}
.btn-add-inline:hover{background:var(--accent);color:#fff}
.btn-add-inline-select{font-size:10px;font-weight:600;padding:2px 8px;background:#fff;color:var(--accent);border:1px solid var(--accent);border-radius:4px;cursor:pointer;line-height:1.4;outline:none;max-width:260px}
.btn-add-inline-select:hover{background:var(--accent-soft)}
.btn-add-inline-sm{font-size:8.5px;font-weight:600;padding:1px 6px;background:#f3f4f6;color:#374151;border:1px solid var(--line);border-radius:3px;cursor:pointer;margin-left:auto}
.btn-add-inline-sm:hover{background:var(--accent-soft);color:var(--accent);border-color:var(--accent)}
.calc{background:#f3f4f6 !important;color:#374151;font-weight:600}
table.hsn th{background:#374151}
table.msched{width:100%;table-layout:fixed;border-collapse:collapse;box-sizing:border-box}
table.msched th,table.msched td{padding:5px 3.5px;font-size:9.5px;box-sizing:border-box;vertical-align:middle}
table.msched th{background:#374151;font-size:8.5px;letter-spacing:.2px;padding:5px 3px;line-height:1.2;white-space:normal}
table.msched th.c,table.msched td.c{text-align:center}
table.msched th.n,table.msched td.n{text-align:right}
table.msched th.desccol,table.msched td.desccol{text-align:left}
table.msched td.desccol{white-space:normal;word-break:break-word;overflow-wrap:break-word}
table.msched td.n,table.msched td.c{white-space:nowrap}

.tot-wrap{display:flex;justify-content:space-between;gap:12px;margin-top:11px;align-items:flex-start}
.leftcol{flex:1;display:flex;flex-direction:column;gap:8px;min-width:0}
.words{border:1px solid var(--line);border-radius:var(--radius);padding:7px 10px;font-size:10.4px}
.words .lbl{font-size:9.5px;text-transform:uppercase;letter-spacing:.8px;color:var(--muted);font-weight:700}
.words .val{font-weight:600}
.rightcol{width:78mm;flex:none}
.tot{border:1px solid var(--line);border-radius:var(--radius);overflow:hidden}
.tot .r{display:flex;justify-content:space-between;gap:8px;padding:4.5px 10px;font-size:10.8px;border-bottom:1px solid #edeff3}
.tot .r span:last-child{font-variant-numeric:tabular-nums;font-weight:600;white-space:nowrap}
.tot .r.grand{background:var(--accent);color:#fff;font-size:13.5px;font-weight:700;padding:8px 10px;border:0}
.tot .r.disc span:last-child{color:#b91c1c}
.settle{margin-top:7px;border:1px solid var(--line);border-radius:var(--radius);overflow:hidden}
.settle .r{display:flex;justify-content:space-between;gap:8px;padding:4.5px 10px;font-size:10.8px;border-bottom:1px solid #edeff3}
.settle .r:last-child{border-bottom:0}
.settle .r span:last-child{font-weight:600;font-variant-numeric:tabular-nums;white-space:nowrap}
.settle .r.bal{background:linear-gradient(135deg, #fed7aa 0%, #e9d5ff 100%);color:#4c1d95;font-weight:700;font-size:12px}
.settle .r.tds span:last-child{color:#b91c1c}

.cols{display:flex;gap:10px;margin-top:11px;align-items:stretch}
.pane{flex:1;border:1px solid var(--line);border-radius:var(--radius);padding:8px 10px;min-width:0}
.pane h3{font-size:9.5px;text-transform:uppercase;letter-spacing:.8px;color:var(--accent);font-weight:700;margin-bottom:5px}
.sign{width:56mm;flex:none;border:1px solid var(--line);border-radius:var(--radius);padding:8px 10px;
      display:flex;flex-direction:column}
.sign .sp{flex:1;min-height:38px}
.sign .line{border-top:1px solid #9ca3af;padding-top:4px;font-size:10.4px;font-weight:600}
.sign .role{font-size:9.5px;color:var(--muted)}
ul.list,ol.list{margin:2px 0 0 15px}
ul.list li,ol.list li{font-size:10.2px;line-height:1.6}
.pagefoot{margin-top:12px;padding-top:7px;border-top:1px solid var(--line);
          display:flex;justify-content:space-between;gap:12px;font-size:9.4px;color:var(--muted)}
.opt-off{display:none !important}

@page{
  size:A4 portrait;
  margin:6mm 8mm 6mm 8mm;
}
@media print{
  html,body{
    background:#fff !important;
    padding:0 !important;
    margin:0 !important;
    font-size:13px !important;
    -webkit-print-color-adjust:exact !important;
    print-color-adjust:exact !important;
  }
  .toolbar,.hint,.rowdel,.btn-add-inline,.btn-add-inline-sm,.btn-add-inline-select,select.btn-add-inline-select,.item-actions{
    display:none !important;
  }
  .sheet{
    width:100% !important;
    max-width:100% !important;
    min-height:auto !important;
    margin:0 !important;
    padding:0 !important;
    box-shadow:none !important;
    border:none !important;
    overflow:visible !important;
    box-sizing:border-box !important;
  }
  .sheet+.sheet{margin-top:0 !important;page-break-before:always !important}
  .hdr{
    display:flex !important;
    flex-direction:row !important;
    justify-content:space-between !important;
    align-items:flex-start !important;
    border-bottom:3px solid var(--accent) !important;
    padding-bottom:8px !important;
    gap:16px !important;
  }
  .brand{
    display:flex !important;
    flex-direction:row !important;
    gap:12px !important;
    align-items:flex-start !important;
  }
  .doc{
    text-align:right !important;
    flex:none !important;
  }
  .info{
    display:flex !important;
    flex-direction:row !important;
    gap:9px !important;
    margin:8px 0 6px !important;
    align-items:stretch !important;
  }
  .box{
    border:1px solid var(--line) !important;
    border-radius:var(--radius) !important;
    padding:6px 8px !important;
    flex:1 !important;
    min-width:0 !important;
  }
  .box.acct{
    background:var(--accent-soft) !important;
    border-color:#c8d3ee !important;
    flex:1 !important;
  }
  .sec{
    display:flex !important;
    align-items:center !important;
    gap:8px !important;
    margin:8px 0 4px !important;
  }
  .table-wrap{
    width:100% !important;
    margin:6px 0 !important;
    overflow:visible !important;
  }
  table{
    display:table !important;
    width:100% !important;
    border-collapse:collapse !important;
    table-layout:auto !important;
  }
  th,td{
    border:1px solid var(--line) !important;
    padding:4px 6px !important;
    font-size:10px !important;
  }
  th{
    background:var(--accent) !important;
    color:#fff !important;
    -webkit-print-color-adjust:exact !important;
    print-color-adjust:exact !important;
  }
  .tot-wrap{
    display:flex !important;
    flex-direction:row !important;
    justify-content:space-between !important;
    gap:10px !important;
    margin-top:8px !important;
    align-items:flex-start !important;
  }
  .leftcol{
    flex:1 !important;
    min-width:0 !important;
    display:flex !important;
    flex-direction:column !important;
    gap:6px !important;
  }
  .words{
    border:1px solid var(--line) !important;
    border-radius:var(--radius) !important;
    padding:5px 8px !important;
    font-size:10px !important;
  }
  .rightcol{
    width:78mm !important;
    flex:none !important;
  }
  .tot{
    border:1px solid var(--line) !important;
    border-radius:var(--radius) !important;
  }
  .tot .r{
    padding:3.5px 8px !important;
    font-size:10px !important;
  }
  .settle{
    margin-top:5px !important;
    border:1px solid var(--line) !important;
    border-radius:var(--radius) !important;
  }
  .settle .r{
    padding:3.5px 8px !important;
    font-size:10px !important;
  }
  .cols{
    display:flex !important;
    flex-direction:row !important;
    gap:10px !important;
    margin-top:8px !important;
    align-items:stretch !important;
  }
  .pane{
    flex:1 !important;
    min-width:0 !important;
    border:1px solid var(--line) !important;
    border-radius:var(--radius) !important;
    padding:6px 8px !important;
  }
  .sign{
    width:56mm !important;
    flex:none !important;
    border:1px solid var(--line) !important;
    border-radius:var(--radius) !important;
    padding:6px 8px !important;
  }
  .pagefoot{
    margin-top:8px !important;
    padding-top:5px !important;
    border-top:1px solid var(--line) !important;
    display:flex !important;
    justify-content:space-between !important;
    gap:12px !important;
    font-size:9px !important;
  }
  [contenteditable]:hover,[contenteditable]:focus{background:none !important;box-shadow:none !important}
  [data-ph]:empty:before{content:'' !important}
  tr{page-break-inside:avoid}
  thead{display:table-header-group}
  .tot-wrap,.cols,table.hsn{page-break-inside:avoid}
}
</style>
<script>
window.initLogo = function initLogo(){
  try {
    var saved = localStorage.getItem('dt_company_logo') || localStorage.getItem('dt_letterhead_logo');
    var slot = document.getElementById('logoSlot');
    if(slot && saved && !slot.querySelector('img')){
      slot.innerHTML = '<img src="' + saved + '" style="max-height:100%;max-width:100%;object-fit:contain"/>';
      slot.classList.add('has-logo');
    }
  } catch(e){}
};
</script>
</head>
<body data-doc="${docTypeVal}">

<div class="sheet" id="sheet" data-doc="${docTypeVal}" data-stage-scale="${stageScale}" data-header-discount="${data.headerDiscount || 0}" data-header-discount-type="${data.headerDiscountType || 'percent'}" data-additional-charges="${Number(data.additionalCharges || 0) || 0}" data-additional-charges-label="${(data as any).additionalChargesLabel || 'Additional charges'}" data-tax-summary="${optShowTaxSummary ? '1' : '0'}" data-contract-grand="${fullContractGrand || ''}" data-contract-taxable="${fullContractTaxable || ''}" data-contract-tax="${fullContractTax || ''}">
  <div class="wm ${isProforma ? '' : 'opt-off'}" data-only="proforma" id="wm">PROFORMA</div>
  <div class="body">

  <div class="hdr">
    <div class="brand">
      <div class="logo-slot ${coLogo ? 'has-logo' : ''}" id="logoSlot" style="cursor:default;border:0;background:none">
        ${coLogo ? `<img src="${coLogo}" style="max-height:100%;max-width:100%;object-fit:contain"/>` : ''}
      </div>
      <div>
        <div class="co-name">${coName}</div>
        <div class="co-tag">${coTag}</div>
        <div class="co-meta">
          <div>${coAddr}</div>
          <div>GSTIN: <b>${coGstin}</b> &nbsp;·&nbsp;
               PAN: <b>${coPan}</b> &nbsp;·&nbsp;
               State: <span>${coState}</span></div>
          <div><span>${coPhone}</span> &nbsp;·&nbsp;
               <span>${coEmail}</span> &nbsp;·&nbsp;
               <span>${coWeb}</span></div>
        </div>
      </div>
    </div>
    <div class="doc">
      <h1 id="docTitle">${docTitle}</h1>
      <div class="sub" id="docSub">${docSub}</div>
      <div class="copylabel ${isTax ? '' : 'opt-off'}" data-only="tax" data-copylabel>${copyLabel}</div>
      <div class="copylabel ${isQuotation ? '' : 'opt-off'}" data-only="quotation">Commercial Proposal</div>
      <div class="notax ${isProforma ? '' : 'opt-off'}" data-only="proforma">Not a tax invoice · No input tax credit</div>
    </div>
  </div>

  <div class="info">
    <div class="box">
      <h3 id="billLabel">${isQuotation ? 'Quotation for' : 'Bill to'}</h3>
      <div class="nm" contenteditable>${clientName}</div>
      <div class="ln">
        <div contenteditable>${clientAddr}</div>
        <div>GSTIN: <span contenteditable>${clientGstin}</span></div>
        ${optHidePlaceOfSupply ? '' : `<div>Place of supply: <span ${placeFromGstin ? 'title="From the customer GSTIN"' : 'contenteditable'}>${placeOfSupply}</span>${data.gstMode === 'inter' ? ' <span class="supply-tag" style="display:inline-block;margin-left:4px;padding:0 6px;border:1px solid #bfdbfe;background:#eff6ff;color:#1e40af;border-radius:4px;font-size:9px;font-weight:700;vertical-align:1px">Supply: Inter-State</span>' : ''}</div>`}
        <div>Contact: <span contenteditable>${clientPhone}</span></div>
        ${clientEmail ? `<div>Email: <span contenteditable>${clientEmail}</span></div>` : ''}
      </div>
    </div>
    ${(data.shippingAddress || data.shippingName) ? `
    <div class="box">
      <h3>Ship to</h3>
      <div class="nm" contenteditable>${data.shippingName || 'Shipping address'}</div>
      <div class="ln">
        <div contenteditable>${data.shippingAddress}</div>
        ${data.shippingCity ? `<div>${data.shippingCity}${data.shippingState ? ', ' + data.shippingState : ''}${data.shippingPincode ? ' - ' + data.shippingPincode : ''}</div>` : ''}
      </div>
    </div>` : ''}
    <div class="box acct">
      <h3>Document details</h3>
      <div class="kv"><span id="noLabel">${noLabel}</span><span contenteditable id="dno">${dno}</span></div>
      <div class="kv"><span>Date</span><span contenteditable id="ddate">${ddate}</span></div>
      <div class="kv ${isTax ? '' : 'opt-off'}" data-only="tax"><span>Due date</span><span contenteditable id="ddue">${ddue}</span></div>
      <div class="kv ${isTax ? 'opt-off' : ''}" data-only="valid"><span>Valid until</span><span contenteditable id="dvalid">${dvalid}</span></div>
      ${preparedBy ? `<div class="kv"><span>Prepared by</span><span contenteditable>${preparedBy}</span></div>` : ''}
      <div class="kv"><span>Reverse charge</span><span contenteditable>${revCharge}</span></div>
      <div class="kv"><span>Reference / PO</span><span contenteditable data-ph="PO / quotation no.">${refNo}</span></div>
    </div>
  </div>

  <div class="sec" style="margin-top:14px">
    <h2>Particulars</h2>
  </div>
  <table id="items" data-serial="${optShowSerial ? '1' : '0'}" data-hsn="${optShowHsn ? '1' : '0'}" data-sku="${optShowSku ? '1' : '0'}" data-thumb="${thumbCol ? '1' : '0'}" data-descfull="${optDescFullWidth ? '1' : '0'}" data-unitfull="${optFullUnits ? '1' : '0'}">
    <colgroup>
      ${optShowSerial ? '<col style="width:26px"/>' : ''}
      ${thumbCol ? '<col style="width:56px" class="thumbcol"/>' : ''}
      ${optShowSku ? '<col style="width:62px" class="skucol"/>' : ''}
      <col/>
      ${optShowHsn ? '<col style="width:58px" class="hsncol"/>' : ''}
      <col style="width:46px"/>
      <col style="width:50px"/>
      <col style="width:62px"/>
      <col style="width:40px"/>
      <col style="width:70px"/>
      ${gstCols ? '<col style="width:40px"/><col style="width:70px"/>' : ''}
      ${isProforma && isGstActive ? '<col style="width:85px"/>' : ''}
    </colgroup>
    <thead>
      <tr>
        ${optShowSerial ? '<th class="c">#</th>' : ''}
        ${thumbCol ? '<th class="thumbcol c">Image</th>' : ''}
        ${optShowSku ? '<th class="skucol c">SKU</th>' : ''}
        <th class="desccol">Description of services / goods</th>
        ${optShowHsn ? '<th class="hsncol c">HSN/SAC</th>' : ''}
        <th class="c">Qty</th>
        <th class="c">Unit</th>
        <th class="c">Rate (₹)</th>
        <th class="c">Disc%</th>
        <th class="c">Taxable</th>
        ${gstCols ? '<th class="c">GST%</th><th class="c">Tax amt</th>' : ''}
        ${isProforma && isGstActive ? '<th class="c">Total (₹)</th>' : ''}
      </tr>
    </thead>
    <tbody id="itemsBody">${itemsRowsHtml}</tbody>
    ${optHideSubtotal ? '' : `
    <tfoot>
      <tr>
        <td colspan="${itemsColsBeforeTaxable}" class="n">Total</td>
        <td class="n" id="ftTaxable">${taxableStr}</td>
        ${gstCols ? '<td></td><td class="n" id="ftTax">' + taxTotalStr + '</td>' : ''}
        ${isProforma && isGstActive ? `<td class="n" id="ftTotal">${fmtCurrency(taxableTotal + taxTotal)}</td>` : ''}
      </tr>
    </tfoot>`}
  </table>

  <div class="tot-wrap">
    <div class="leftcol">
      <div class="words">
        <div class="lbl">Amount in words</div>
        <div class="val" id="words">${amountInWords(grandTotal, data.currency || '₹')}</div>
      </div>
      <div class="words">
        <div class="lbl">Notes</div>
        <ul class="list" id="notes" contenteditable>${notesHtml}</ul>
      </div>
      ${(isQuotation && !isMilestone && String(data.paymentTerms || '').trim()) ? `<div class="words" data-opt="payterms">
        <div class="lbl">Payment Terms</div>
        <div class="val" id="payTerms" style="font-weight:400"><span style="font-weight:600">${escapeAttr(String(data.paymentTerms).trim())}</span>${String(data.paymentTermsDescription || '').trim() ? ` — ${escapeAttr(String(data.paymentTermsDescription).trim())}` : ''}</div>
      </div>` : ''}
      <div class="words">
        <div class="lbl">Terms &amp; Conditions</div>
        <ul class="list" id="terms" contenteditable>${termsHtml}</ul>
      </div>
    </div>
    <div class="rightcol">
      <div class="tot" id="totals">${totalsHtml}</div>
      <div class="settle">
        <div class="r"><span id="recvLabel">${isProforma ? 'Advance received' : 'Amount received'}</span><span><span contenteditable id="received">${fmtCurrency(amtReceived)}</span></span></div>
        <div class="r tds opt-off" data-opt="tds">
          <span>Less: TDS @ <span contenteditable id="tdsRate">${tdsRate || 2}</span>% (u/s 194J, informational)</span>
          <span id="tdsAmt">${tdsAmtStr}</span>
        </div>
        <div class="r bal"><span id="balLabel">${isProforma ? 'Advance now payable' : 'Balance due'}</span><span id="balance" data-extra-due="${otherStagesDue}">${balanceStr}</span></div>
      </div>
    </div>
  </div>

${milestonesSectionHtml}${finalBillsHtml}

  ${((assumptionsHtml || isQuotation) && (data as any).assumptionsEnabled !== false) ? `
  <div data-opt="scope" style="margin-top:12px">
    <div class="sec"><h2>Assumptions &amp; Exclusions</h2></div>
    <ul class="list" contenteditable>
      ${assumptionsHtml}
    </ul>
  </div>` : ''}

  <div class="cols">
    <div class="pane">
      <h3>Bank details</h3>
      <div class="kv"><span>Account name</span><span>${bankAccName}</span></div>
      <div class="kv"><span>Account no.</span><span>${bankAccNo}</span></div>
      <div class="kv"><span>Bank</span><span>${bankName}</span></div>
      <div class="kv"><span>Branch</span><span>${bankBranch}</span></div>
      <div class="kv"><span>IFSC</span><span>${bankIfsc}</span></div>
      <div class="kv"><span>UPI</span><span>${bankUpi}</span></div>
    </div>
    <div class="pane">
      <h3>Declaration</h3>
      <div style="font-size:10.2px;line-height:1.6" id="declaration">${declaration}</div>
    </div>
    <div class="sign">
      <div style="font-size:9.5px;text-transform:uppercase;letter-spacing:.8px;color:var(--accent);font-weight:700">For ${coName}</div>
      <div class="sp" style="flex:1;min-height:42px;display:flex;align-items:center;justify-content:center">
        ${signatureUrl ? `<img src="${signatureUrl}" alt="Authorised Signature" style="max-height:46px;max-width:140px;object-fit:contain" />` : ''}
      </div>
      <div class="line">${signatory}</div>
      <div class="role">${signatoryRole}</div>
    </div>
  </div>

  <div class="pagefoot">
    <span>${footerLeft || [coName, coWeb, coEmail, coPhone].filter(Boolean).join(' · ')}</span>
    <span id="footNote">${footerRight || (isProforma ? 'This proforma invoice is issued for payment purposes only. It is not a tax invoice and no input tax credit may be claimed against it.' : isTax ? '' : 'This is a computer-generated quotation.')}</span>
  </div>

  </div>
</div>

<script>
const $  = (s,r=document)=>r.querySelector(s);
const $$ = (s,r=document)=>[...r.querySelectorAll(s)];
const num = (el)=>{ const v=parseFloat(String(el?.textContent||'').replace(/[^0-9.\-]/g,'')); return isNaN(v)?0:v; };
const cur = ()=>$('#cur') ? ($('#cur').value||'') : (document.documentElement.dataset.cur||'₹');
const gstMode = ()=>$('#gstMode') ? $('#gstMode').value : (document.documentElement.dataset.gst||'intra');
const docType = ()=>$('#docType') ? $('#docType').value : (document.documentElement.dataset.doc||'tax');
const isINR = ()=>cur()==='₹';
const fmt = (n)=>cur()+Number(n||0).toLocaleString(isINR()?'en-IN':'en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
const r2 = (n)=>Math.round((Number(n)||0)*100)/100;

const UNIT_FULL_MAP={no:'Numbers',nos:'Numbers',pcs:'Pieces',pc:'Pieces',pkg:'Package',hr:'Hours',hrs:'Hours',wk:'Weeks',wks:'Weeks',mo:'Months',mos:'Months',yr:'Years',yrs:'Years',kg:'Kilograms',km:'Kilometres',m:'Metres',cm:'Centimetres',ltr:'Litres',set:'Sets',box:'Boxes',pkt:'Packets'};
function fullUnit(u){ const raw=String(u||'Nos').trim(); const k=raw.toLowerCase().replace(/\\.$/,''); return UNIT_FULL_MAP[k]||raw; }

function addRow(desc='',sub='',hsn='998314',qty='1',unit='Nos',rate='0',disc='0',gst='18'){
  let b = $('#itemsBody') || $('#items tbody') || $('table tbody');
  if(!b && $('#items')){
    b = document.createElement('tbody');
    b.id = 'itemsBody';
    $('#items').appendChild(b);
  }
  const ds = $('#items') ? $('#items').dataset : {};
  const showSerial = ds.serial !== '0';
  const showHsn = ds.hsn !== '0';
  const showSku = ds.sku === '1';
  const showThumb = ds.thumb === '1';
  const descFull = ds.descfull === '1';
  if(ds.unitfull === '1') unit = fullUnit(unit);
  const noGst = gstMode() === 'none';
  if(b){
    const gstCells = noGst ? '' : '<td class="n" contenteditable>' + gst + '</td><td class="n calc" data-tax>—</td>';
    // Proforma documents with GST carry an extra per-line Total column — keep new rows aligned.
    const totalCell = (docType()==='proforma' && !noGst) ? '<td class="n calc" data-linetotal>—</td>' : '';
    const serialCell = showSerial ? '<td class="c" data-f="serial">0<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();renumber();recalc()">×</button></td>' : '';
    const thumbCell = showThumb ? '<td class="thumbcol c"></td>' : '';
    const skuCell = showSku ? '<td class="c" contenteditable data-ph="—"></td>' : '';
    const hsnCell = showHsn ? '<td class="hsncol c" contenteditable data-ph="—">' + hsn + '</td>' : '';
    const delBtn = '<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();renumber();recalc()">×</button>';
    const descInner = '<div class="it-name" contenteditable data-ph="Item / service">' + desc + '</div>' + (descFull ? '' : '<div class="it-desc" contenteditable data-ph="Details (optional)">' + sub + '</div>');
    b.insertAdjacentHTML('beforeend',
      '<tr data-item-row="1">' + serialCell + thumbCell + skuCell +
      '<td class="desccol" data-f="desc">' + (showSerial ? '' : delBtn) + descInner + '</td>' + hsnCell +
      '<td class="n" contenteditable data-f="qty">' + qty + '</td>' +
      '<td class="c" contenteditable data-f="unit">' + unit + '</td>' +
      '<td class="n" contenteditable data-f="rate">' + rate + '</td>' +
      '<td class="n" contenteditable data-f="disc">' + disc + '</td>' +
      '<td class="n calc" data-taxable>—</td>' + gstCells + totalCell + '</tr>');
    renumber(); recalc();
  }
}
function renumber(){
  const tbody = $('#itemsBody') || $('#items tbody');
  const rows = tbody ? Array.prototype.slice.call(tbody.querySelectorAll('tr')) : [];
  const itemRows = rows.filter(tr => tr.dataset.itemRow === '1');
  const showSerial = $('#items') ? $('#items').dataset.serial !== '0' : true;
  itemRows.forEach((tr,i)=>{
    const serialCell = tr.querySelector('[data-f="serial"]');
    if(serialCell){
      serialCell.innerHTML = (showSerial ? (i+1) : '') + '<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();renumber();recalc()">×</button>';
    } else if(!showSerial){
      const descCell = tr.querySelector('[data-f="desc"]');
      if(descCell && !descCell.querySelector('.rowdel')){
        descCell.insertAdjacentHTML('afterbegin','<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();renumber();recalc()">×</button>');
      }
    }
  });
}
function addNote(txt=''){
  let notes = $('#notes');
  if(!notes){
    const leftcol = $('.leftcol') || $('.tot-wrap');
    if(leftcol){
      const box = document.createElement('div');
      box.className = 'words';
      box.innerHTML = '<div class="lbl">Notes</div><ul class="list" id="notes" contenteditable></ul>';
      leftcol.appendChild(box);
      notes = $('#notes');
    }
  }
  if(notes){
    const li=document.createElement('li'); li.contentEditable='true'; li.textContent=txt||'New note...'; notes.appendChild(li); li.focus();
  }
}

const ONES=['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
const TENS=['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
const two=x=>x<20?ONES[x]:TENS[Math.floor(x/10)]+(x%10?' '+ONES[x%10]:'');
const three=x=>x>=100?ONES[Math.floor(x/100)]+' Hundred'+(x%100?' '+two(x%100):''):two(x);
function words(amount){
  const unit = cur()==='₹' ? ['Rupees','Paise'] : cur()==='$' ? ['Dollars','Cents'] : ['Euro','Cents'];
  const abs=Math.abs(Number(amount)||0), n=Math.floor(abs), p=[];
  if(!abs) return ('Zero '+unit[0]+' Only').replace(/\\s+/g,' ');
  if(isINR()){
    const cr=Math.floor(n/1e7), la=Math.floor((n%1e7)/1e5), th=Math.floor((n%1e5)/1e3), r=n%1e3;
    if(cr)p.push(three(cr)+' Crore'); if(la)p.push(three(la)+' Lakh');
    if(th)p.push(three(th)+' Thousand'); if(r)p.push(three(r));
  } else {
    const bn=Math.floor(n/1e9), mn=Math.floor((n%1e9)/1e6), th=Math.floor((n%1e6)/1e3), r=n%1e3;
    if(bn)p.push(three(bn)+' Billion'); if(mn)p.push(three(mn)+' Million');
    if(th)p.push(three(th)+' Thousand'); if(r)p.push(three(r));
  }
  const ps=Math.round((abs-n)*100);
  return (p.join(' ')+' '+unit[0]+(ps?' and '+two(ps)+' '+unit[1]:'')+' Only').replace(/\\s+/g,' ').trim();
}

function recalc(){
  try {
  const mode=gstMode();
  document.documentElement.dataset.gst = mode;
  let gross=0, discount=0, taxable=0, tax=0;
  const slabs={};

  $$('#itemsBody tr').forEach(tr=>{
    if(tr.dataset.itemRow !== '1') return;
    const cellOf = (f)=>tr.querySelector('[data-f="'+f+'"]');
    const qty=num(cellOf('qty')), rate=num(cellOf('rate')), disc=num(cellOf('disc'));
    // Lines billed by duration (Hours, Days, Weeks, Months, Years): the rate is the price for the whole period.
    const isDuration=/^(hours?|hrs?|days?|weeks?|wks?|months?|mos?|years?|yrs?)$/i.test((cellOf('unit')?.textContent||'').trim());
    // The GST% cell sits directly before the data-tax cell — derive it from there instead
    // of a positional nth-child selector, which breaks whenever the column layout shifts.
    const taxAmtCell = tr.querySelector('[data-tax]');
    const gstCell = taxAmtCell ? taxAmtCell.previousElementSibling : tr.querySelector('[contenteditable]:nth-child(9)');
    const storedTax = taxAmtCell ? num(taxAmtCell) : 0;
    let gst = mode==='none' ? 0 : num(gstCell);
    const line=(isDuration&&qty>0)?rate:qty*rate, d=line*disc/100, tv=r2(line-d);
    // If the GST% cell can't be read, fall back to the server-rendered tax amount so the
    // stored document keeps its tax instead of silently dropping it to zero.
    if (mode!=='none' && !gst && storedTax>0 && tv>0) gst = r2(storedTax/tv*100);
    const t=mode==='none'?0:r2(tv*gst/100);
    gross+=line; discount+=d; taxable+=tv; tax+=t;
    if(mode!=='none'&&t) slabs[gst]=r2((slabs[gst]||0)+t);

    const taxCell = tr.querySelector('[data-taxable]');
    if(taxCell) taxCell.textContent=fmt(tv);
    if(taxAmtCell) taxAmtCell.textContent=mode==='none'?'—':fmt(t);
    if(gstCell) gstCell.style.opacity = mode==='none'?.35:1;
    const lineTotalCell = tr.querySelector('[data-linetotal]');
    if(lineTotalCell) lineTotalCell.textContent = mode==='none' ? fmt(tv) : fmt(r2(tv+t));
  });

  const stageScale = parseFloat(document.documentElement.dataset.stageScale || $('#sheet')?.dataset?.stageScale || '1') || 1;
  // Dataset values are raw strings — parseFloat them directly (num() reads an element's textContent).
  const hDiscVal = parseFloat(String($('#sheet')?.dataset?.headerDiscount ?? '0').replace(/[^0-9.\-]/g,'')) || 0;
  const hDiscType = $('#sheet')?.dataset?.headerDiscountType || 'percent';
  // Additional charges are a payable row and part of the grand total — read them from the
  // sheet dataset so the live rebuild matches the stored document total.
  const addlCharges = parseFloat(String($('#sheet')?.dataset?.additionalCharges ?? '0').replace(/[^0-9.\-]/g,'')) || 0;
  const addlChargesLabel = $('#sheet')?.dataset?.additionalChargesLabel || 'Additional charges';
  let hDiscAmt = 0;
  if (hDiscVal > 0) {
    if (hDiscType === 'percent') {
      hDiscAmt = r2((gross - discount) * (hDiscVal / 100));
    } else {
      hDiscAmt = r2(hDiscVal * stageScale);
    }
  }
  const totalDisc = r2(discount + hDiscAmt);
  taxable = Math.max(0, r2(gross - totalDisc));
  const lineTaxable = r2(gross - discount);
  const taxScale = lineTaxable > 0 ? taxable / lineTaxable : 1;
  tax = mode === 'none' ? 0 : r2(tax * taxScale);
  if (mode !== 'none') {
    Object.keys(slabs).forEach(rt => {
      slabs[rt] = r2(slabs[rt] * taxScale);
    });
  }

  taxable=r2(taxable); tax=r2(tax); gross=r2(gross); discount=totalDisc;
  if($('#ftTaxable')) $('#ftTaxable').textContent=fmt(taxable);
  if($('#ftTax')) $('#ftTax').textContent=mode==='none'?'—':fmt(tax);
  if($('#ftTotal')) $('#ftTotal').textContent=mode==='none'?fmt(taxable):fmt(r2(taxable+tax));

  const pre=r2(taxable+tax+(addlCharges>0?addlCharges:0));
  const round=r2(Math.round(pre)-pre);
  const grand=r2(pre+round);

  const rows=[['Gross value',fmt(gross)]];
  if(totalDisc) rows.push(['Discount','− '+fmt(totalDisc),'disc']);
  rows.push(['Total value',fmt(taxable)]);
  const showTaxSummary = $('#sheet')?.dataset?.taxSummary !== '0';
  if(mode!=='none' && showTaxSummary) Object.keys(slabs).sort((a,b)=>a-b).forEach(rt=>{
    if(mode==='intra'){
      const half=r2(slabs[rt]/2);
      rows.push([\`CGST @ \${rt/2}%\`,fmt(half)]);
      rows.push([\`SGST @ \${rt/2}%\`,fmt(r2(slabs[rt]-half))]);
    } else {
      rows.push([\`IGST @ \${rt}%\`,fmt(slabs[rt])]);
    }
  });
  if(addlCharges>0) rows.push([addlChargesLabel,'+ '+fmt(addlCharges)]);
  if(Math.abs(round)>=0.005) rows.push(['Round off',(round<0?'− ':'+ ')+fmt(Math.abs(round))]);
  if($('#totals')) {
    $('#totals').innerHTML = rows.map(([k,v,c])=>\`<div class="r \${c||''}"><span>\${k}</span><span>\${v}</span></div>\`).join('')
      + \`<div class="r grand"><span>\${docType()==='proforma'?'Total payable':'Grand Total'}</span><span>\${fmt(grand)}</span></div>\`;
  }
  if($('#words')) $('#words').textContent = words(grand);

  const recv=num($('#received'));
  const tdsOn=$('#optTds') ? $('#optTds').value==='1' : !$('[data-opt="tds"]').classList.contains('opt-off');
  const tdsAmt=tdsOn ? r2(taxable*num($('#tdsRate'))/100) : 0;
  if($('#tdsAmt')) $('#tdsAmt').textContent='− '+fmt(tdsAmt);
  if($('#balance')) $('#balance').textContent=fmt(r2(grand-recv-tdsAmt+(parseFloat($('#balance').getAttribute('data-extra-due'))||0)));

  let schTotTaxable = 0, schTotCgst = 0, schTotSgst = 0, schTotIgst = 0, schTotTax = 0, schTotGrand = 0, schTotPct = 0;
  const hasCurrentStageRow = Boolean($('#schedBody tr[data-is-current="1"]'));

  const baseTaxable = stageScale > 0 && stageScale < 1 ? r2(taxable / stageScale) : taxable;
  const baseGrand   = stageScale > 0 && stageScale < 1 ? r2(grand / stageScale) : grand;
  const baseTax     = stageScale > 0 && stageScale < 1 ? r2(tax / stageScale) : tax;

  const effectiveGstPct = baseTaxable > 0 ? r2((baseTax / baseTaxable) * 100) : 18;
  const cgstRatePct = mode === 'intra' ? r2(effectiveGstPct / 2) : 0;
  const sgstRatePct = cgstRatePct;

  $$('#schedBody tr').forEach(tr => {
    const pct = num(tr.cells[2]) || 0;
    schTotPct += pct;
    const isCurrentRow = tr.dataset.isCurrent === '1' || (hasCurrentStageRow ? false : (stageScale > 0 && stageScale < 1 && pct === r2(stageScale * 100)));

    let msTaxable = 0, msCgst = 0, msSgst = 0, msTax = 0, msAmt = 0;

    const cellTaxable = tr.querySelector('[data-mstaxable]');
  const cellCgst = tr.querySelector('[data-mscgst]');
  const cellSgst = tr.querySelector('[data-mssgst]');
  const cellIgst = tr.querySelector('[data-msigst]');
  const cellTax = tr.querySelector('[data-mstax]');
  const cellAmt = tr.querySelector('[data-msamt]');
  const msTaxableVal = cellTaxable ? num(cellTaxable) : (num(tr.cells[3]) || 0);
  const msPct = tr.cells[2] ? (num(tr.cells[2]) || 0) : 0;

  if (isCurrentRow && stageScale > 0 && stageScale < 1) {
    msTaxable = taxable;
    msCgst    = mode === 'intra' ? r2(tax / 2) : 0;
    msSgst    = mode === 'intra' ? r2(tax - msCgst) : 0;
    msTax     = mode === 'none' ? 0 : tax;
    msAmt     = grand;
  } else {
    msTaxable = r2(baseTaxable * (msPct / 100));
    msCgst    = mode === 'intra' ? r2((baseTax / 2) * (msPct / 100)) : 0;
    msSgst    = mode === 'intra' ? r2((baseTax / 2) * (msPct / 100)) : 0;
    msTax     = mode === 'none' ? 0 : (mode === 'intra' ? r2(msCgst + msSgst) : r2(baseTax * (msPct / 100)));
    msAmt     = r2(baseGrand * (msPct / 100));
  }

  schTotTaxable += msTaxable;
  schTotCgst += msCgst;
  schTotSgst += msSgst;
  schTotTax += msTax;
  schTotIgst += mode === 'inter' ? msTax : 0;
  schTotGrand += msAmt;

  if (cellTaxable) cellTaxable.textContent = fmt(msTaxable);
  if (cellCgst) cellCgst.innerHTML = fmt(msCgst) + ' <small style="color:#6b7280">(' + cgstRatePct + '%)</small>';
  if (cellSgst) cellSgst.innerHTML = fmt(msSgst) + ' <small style="color:#6b7280">(' + sgstRatePct + '%)</small>';
  if (cellIgst) cellIgst.innerHTML = fmt(mode === 'inter' ? msTax : 0) + ' <small style="color:#6b7280">(' + effectiveGstPct + '%)</small>';
  if (cellTax) cellTax.textContent = fmt(msTax);
  if (cellAmt) cellAmt.textContent = fmt(msAmt);
});

  if ($('#msTotalPct')) $('#msTotalPct').textContent = schTotPct + '%';
  if ($('#msTotalTaxable')) $('#msTotalTaxable').textContent = fmt(schTotTaxable);
  if ($('#msTotalCgst')) $('#msTotalCgst').innerHTML = fmt(schTotCgst);
  if ($('#msTotalSgst')) $('#msTotalSgst').innerHTML = fmt(schTotSgst);
  if ($('#msTotalIgst')) $('#msTotalIgst').innerHTML = fmt(schTotIgst);
  if ($('#msTotalTax')) $('#msTotalTax').textContent = fmt(schTotTax);
  if ($('#msTotalGrand')) $('#msTotalGrand').textContent = fmt(schTotGrand);
  } catch(e) {
    console.error('recalc error:', e);
  }
}

function addMilestone(name='',pct='0',due=''){
  let b = $('#schedBody') || $('.msched tbody');
  if(!b){
    const sec = document.createElement('div');
    sec.id = 'schedOptSection';
    sec.setAttribute('data-opt', 'sched');
    sec.innerHTML = \`
      <div class="sec" style="margin-top:14px;display:flex;align-items:center;justify-content:space-between">
        <h2>Payment Schedule &amp; Tax Division (Milestone Based)</h2>
      </div>
      <div class="table-wrap" style="width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;margin:10px 0">
      <table class="msched">
        <colgroup>
          <col style="width:24px"/><col/><col style="width:48px"/><col style="width:78px"/><col class="col-intra" style="width:66px"/><col class="col-intra" style="width:66px"/><col class="col-inter" style="width:74px"/><col class="col-intra" style="width:66px"/><col style="width:82px"/><col style="width:72px"/>
        </colgroup>
        <thead><tr>
          <th class="c">#</th><th class="desccol" style="text-align:left">Milestone / Stage</th><th class="c">% Total</th><th class="n">Taxable (INR)</th><th class="n col-intra">CGST</th><th class="n col-intra">SGST</th><th class="n col-inter">IGST</th><th class="n col-intra">Total Tax</th><th class="n" style="line-height:1.2">Amount<br><span style="font-size:7.5px;opacity:0.9">(Incl. GST)</span></th><th class="c">Due / Trigger</th>
        </tr></thead>
        <tbody id="schedBody"></tbody>
        <tfoot>
          <tr style="background:#f3f4f6;font-weight:700">
            <td colspan="2" class="n">Schedule Total</td>
            <td class="n" id="msTotalPct">0%</td>
            <td class="n" id="msTotalTaxable">₹0.00</td>
            <td class="n col-intra" id="msTotalCgst">₹0.00 <small style="color:#6b7280">(9%)</small></td>
            <td class="n col-intra" id="msTotalSgst">₹0.00 <small style="color:#6b7280">(9%)</small></td>
            <td class="n col-inter" id="msTotalIgst">₹0.00</td>
            <td class="n col-intra" id="msTotalTax">₹0.00</td>
            <td class="n" id="msTotalGrand" style="color:#1e3a8a">₹0.00</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      </div>\`;
    const totWrap = $('.tot-wrap') || $('#totals')?.closest('.tot-wrap');
    if(totWrap && totWrap.parentNode){
      totWrap.parentNode.insertBefore(sec, totWrap.nextSibling);
    } else {
      ($('.body') || document.body).appendChild(sec);
    }
    b = $('#schedBody');
  }
  if(b){
    b.insertAdjacentHTML('beforeend',\`
      <tr>
        <td class="c">0<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();renumberSched();recalc()">×</button></td>
        <td contenteditable data-ph="Stage name">\${name || 'New Stage'}</td>
        <td class="n" contenteditable>\${pct || '0'}%</td>
        <td class="n calc" data-mstaxable>—</td>
        <td class="n calc col-intra" data-mscgst>—</td>
        <td class="n calc col-intra" data-mssgst>—</td>
        <td class="n calc col-inter" data-msigst>—</td>
        <td class="n calc col-intra" data-mstax>—</td>
        <td class="n calc" data-msamt>—</td>
        <td class="n" contenteditable data-ph="On signing / T+30">\${due || '—'}</td>
      </tr>\`);
    renumberSched(); recalc();
  }
}
function renumberSched(){
  const rows = $$('#schedBody tr').length ? $$('#schedBody tr') : $$('.msched tbody tr');
  rows.forEach((tr,i)=>{
    if(tr.cells && tr.cells[0]){
      tr.cells[0].innerHTML=(i+1)+'<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();renumberSched();recalc()">×</button>';
    }
  });
}
function addTerm(txt=''){
  let list = $('#terms');
  if(!list){
    const leftcol = $('.leftcol') || $('.tot-wrap');
    if(leftcol){
      const box = document.createElement('div');
      box.className = 'words';
      box.innerHTML = '<div class="lbl">Terms &amp; Conditions (Payment terms, delivery timeline, warranty...)</div><ul class="list" id="terms" contenteditable></ul>';
      leftcol.appendChild(box);
      list = $('#terms');
    }
  }
  if(list){
    const li=document.createElement('li');
    li.contentEditable='true';
    li.textContent=txt||'Payment terms, delivery timeline, warranty...';
    list.appendChild(li);
    li.focus();
  }
}
function selectCatalogItem(selectEl){
  if(!selectEl || !selectEl.value) return;
  var val = selectEl.value;
  if(val === '__custom__'){
    addRow('New Item / Service', '', '998314', 1, 'Nos', 0, 0, 18);
    selectEl.value = '';
    return;
  }
  var opt = selectEl.selectedOptions ? selectEl.selectedOptions[0] : null;
  var name = opt ? (opt.getAttribute('data-name') || opt.textContent) : val;
  var desc = opt ? (opt.getAttribute('data-desc') || '') : '';
  var hsn = opt ? (opt.getAttribute('data-hsn') || '998314') : '998314';
  var rate = opt ? (opt.getAttribute('data-rate') || '0') : '0';
  var unit = opt ? (opt.getAttribute('data-unit') || 'Nos') : 'Nos';
  var gst = opt ? (opt.getAttribute('data-gst') || '18') : '18';
  var disc = opt ? (opt.getAttribute('data-disc') || '0') : '0';

  if(window.CATALOG_ITEMS && Array.isArray(window.CATALOG_ITEMS)){
    var found = window.CATALOG_ITEMS.find(function(p){
      return String(p._id || p.id) === String(val) || p.name === val;
    });
    if(found){
      name = found.name || name;
      hsn = found.hsnSac || found.hsnCode || found.hsn || hsn;
      rate = found.sellingPrice !== undefined ? found.sellingPrice : (found.rate !== undefined ? found.rate : rate);
      unit = (found.unit && typeof found.unit === 'object') ? (found.unit.shortName || found.unit.name || 'Nos') : (found.unit || unit);
      if(found.taxProfileId && typeof found.taxProfileId === 'object'){
        var c = found.taxProfileId.cgstRate || 0;
        var s = found.taxProfileId.sgstRate || 0;
        var i = found.taxProfileId.igstRate || 0;
        gst = i || (c + s) || gst;
      } else if(found.taxRate !== undefined){
        gst = found.taxRate;
      } else if(found.gst !== undefined){
        gst = found.gst;
      }
    }
  }

  if(val !== '__custom__'){
    var rows = $$('#itemsBody tr').length ? $$('#itemsBody tr') : $$('#items tbody tr');
    for(var idx = 0; idx < rows.length; idx++){
      var row = rows[idx];
      if(row.dataset.itemRow !== '1') continue;
      var nameEl = row.querySelector('.it-name');
      var rowName = nameEl ? nameEl.textContent.trim() : (row.cells && row.cells[1] ? row.cells[1].textContent.trim() : '');
      if(rowName && rowName.toLowerCase() === String(name).trim().toLowerCase()){
        var qtyCell = row.querySelector('[data-f="qty"]');
        if(qtyCell){
          var currentQty = parseFloat(String(qtyCell.textContent || '').replace(/[^0-9.]/g, '')) || 1;
          qtyCell.textContent = String(currentQty + 1);
          recalc();
          selectEl.value = '';
          return;
        }
      }
    }
  }

  addRow(name, desc, hsn, 1, unit, rate, disc, gst);
  selectEl.value = '';
}

function updateCatalogDropdowns(items){
  if(!items || !items.length) return;
  window.CATALOG_ITEMS = items;
  var selects = $$('.btn-add-inline-select');
  selects.forEach(function(sel){
    var curVal = sel.value;
    var isToolbar = sel.id === 'toolbarItemSelect';
    var defaultText = isToolbar ? '+ Add Item from Catalog...' : '+ Select Item / Product to Add';
    var html = '<option value="">' + defaultText + '</option>';
    items.forEach(function(p){
      var id = p._id || p.id || p.name;
      var name = p.name || p.description || 'Item';
      var desc = '';
      var hsn = p.hsnSac || p.hsnCode || p.hsn || '998314';
      var rate = p.sellingPrice !== undefined ? p.sellingPrice : (p.rate !== undefined ? p.rate : 0);
      var unit = (p.unit && typeof p.unit === 'object') ? (p.unit.shortName || p.unit.name || 'Nos') : (p.unit || 'Nos');
      var gst = 18;
      if(p.taxProfileId && typeof p.taxProfileId === 'object'){
        var c = p.taxProfileId.cgstRate || 0;
        var s = p.taxProfileId.sgstRate || 0;
        var i = p.taxProfileId.igstRate || 0;
        gst = i || (c + s) || 18;
      } else if(p.taxRate !== undefined){
        gst = p.taxRate;
      } else if(p.gst !== undefined){
        gst = p.gst;
      }
      var label = name + (rate > 0 ? ' (₹' + Number(rate).toLocaleString('en-IN') + ')' : '');
      html += '<option value="' + String(id).replace(/"/g, '&quot;') + '"' +
        ' data-name="' + String(name).replace(/"/g, '&quot;') + '"' +
        ' data-desc="' + String(desc).replace(/"/g, '&quot;') + '"' +
        ' data-hsn="' + String(hsn).replace(/"/g, '&quot;') + '"' +
        ' data-rate="' + rate + '"' +
        ' data-unit="' + String(unit).replace(/"/g, '&quot;') + '"' +
        ' data-gst="' + gst + '"' +
        ' data-disc="0">' + String(label).replace(/"/g, '&quot;') + '</option>';
    });
    html += '<option value="__custom__">+ Add Custom / Blank Item</option>';
    sel.innerHTML = html;
    sel.value = curVal;
  });
}

window.addRow = addRow;
window.selectCatalogItem = selectCatalogItem;
window.updateCatalogDropdowns = updateCatalogDropdowns;
window.renumber = renumber;
window.addMilestone = addMilestone;
window.renumberSched = renumberSched;
window.addNote = addNote;
window.addTerm = addTerm;
window.initLogo = initLogo;
window.recalc = recalc;

if(typeof fetch === 'function'){
  fetch('/api/v1/goodday/catalog/products?limit=200')
    .then(function(r){ return r.json(); })
    .then(function(res){
      var items = (res && res.data && Array.isArray(res.data)) ? res.data : (Array.isArray(res) ? res : []);
      if(items.length > 0) updateCatalogDropdowns(items);
    })
    .catch(function(){
      try {
        if(window.parent && window.parent.__CATALOG_PRODUCTS__ && window.parent.__CATALOG_PRODUCTS__.length){
          updateCatalogDropdowns(window.parent.__CATALOG_PRODUCTS__);
        }
      } catch(e){}
    });
}

if(document.readyState==='loading'){ document.addEventListener('DOMContentLoaded',initLogo); } else { setTimeout(initLogo,50); }
document.addEventListener('input',e=>{ if(e.target.closest('#items,#schedBody,#terms,#notes')||e.target.id==='received') recalc(); });
renumber(); renumberSched(); recalc();
</script>
</body>
</html>`;

  return (data.isConfirmed || data.isLocked) ? lockHtmlTemplate(finalHtml) : finalHtml;
}

export function lockHtmlTemplate(html: string): string {
  if (!html) return html;
  return html
    .replace(/\scontenteditable(?:="[^"]*"|='[^']*'|=[^\s>]*)?/gi, '')
    .replace(/<button class="rowdel"[^>]*>.*?<\/button>/gi, '')
    .replace(/<button class="btn btn-add"[^>]*>.*?<\/button>/gi, '')
    .replace(/<button[^>]*class="[^"]*rowdel[^"]*"[^>]*>.*?<\/button>/gi, '')
    .replace(/<div class="row-actions"[^>]*>.*?<\/div>/gi, '');
}

export function generateFullTaxInvoiceHtml(data: Parameters<typeof generateFullInvoiceHtml>[0]): string {
  return generateFullInvoiceHtml({ ...data, docType: 'tax' });
}

export function generateFullProformaInvoiceHtml(data: Parameters<typeof generateFullInvoiceHtml>[0]): string {
  return generateFullInvoiceHtml({ ...data, docType: 'proforma' });
}

